import React, { useState, useEffect, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import MarketplacePage from "./MarketplacePage";
import {
  HexTile, Resources, BuildingType, BUILDING_CONFIGS, MissileType,
  TECH_TREE, SOCKET_EVENTS, GovernmentStance, MISSILE_CONFIG,
} from "@ironpact/shared";
import { HexMap } from "./game/HexMap";
import { unlockAudio, sfxTap, sfxBuild, sfxResearch, sfxAttack, sfxMissileLaunch, sfxMissileImpact, sfxRecruit, sfxError, sfxClaim, sfxChat, sfxDiplomacy, sfxIncoming, sfxTrain, stopSiren, toggleMute, isMuted } from "./audio";
// === Types ===
interface PlayerInfo { id: string; name: string; color: string; reputation: number; online: boolean; lastSeen?: number; stance?: string; dormancyStatus?: string; returnPeaceUntil?: number; }
interface PlayerFull extends PlayerInfo { resources: Resources; techResearched: string[]; capitalHex: { q: number; r: number }; territory: { q: number; r: number }[]; spies?: any[]; stability?: number; }
type AuthState = "checking" | "login" | "register" | "game";

// Material Symbol helper

// Haptic feedback
const haptic = (type: "light" | "medium" | "heavy" | "success" | "error") => {
  if (!navigator.vibrate) return;
  const patterns: Record<string, number | number[]> = {
    light: 10, medium: 25, heavy: 50, success: [15, 50, 15], error: [30, 30, 30, 30, 30]
  };
  try { navigator.vibrate(patterns[type] || 15); } catch {}
};

const MS = ({ icon, className = "", filled = false }: { icon: string; className?: string; filled?: boolean }) => (
  <span className={`material-symbols-outlined ${filled ? "msym-filled" : ""} ${className}`}>{icon}</span>
);

// === Hex math ===
function hexDistance(q1: number, r1: number, q2: number, r2: number): number {
  return Math.max(Math.abs(q1 - q2), Math.abs(r1 - r2), Math.abs((q1 + r1) - (q2 + r2)));
}

// === Utility functions ===
function showFloatingNumber(text: string, color: string, x: number, y: number) {
  const el = document.createElement("div"); el.className = "ip-float-num"; el.style.color = color; el.style.left = x + "px"; el.style.top = y + "px"; el.textContent = text; document.body.appendChild(el); setTimeout(() => el.remove(), 1500);
}
function screenShake(nuclear = false) { const r = document.getElementById("root"); if (!r) return; r.className = nuclear ? "ip-nuke-shake" : "ip-shake"; setTimeout(() => { r.className = ""; }, nuclear ? 1200 : 400); }
function screenFlash(color: "red" | "green" | "gold" | "white") { const el = document.createElement("div"); el.className = "ip-flash-" + color; document.body.appendChild(el); setTimeout(() => el.remove(), 600); }
function showToast(message: string, type: "default" | "danger" | "success" | "epic" = "default", durationMs = 3000) {
  const c = document.getElementById("ip-toast-container"); if (!c) return;
  const t = document.createElement("div"); t.className = "ip-toast" + (type !== "default" ? " ip-toast-" + type : ""); t.textContent = message; c.appendChild(t);
  setTimeout(() => { t.classList.add("ip-toast-out"); setTimeout(() => t.remove(), 300); }, durationMs);
}
function checkResourceChanges(prev: Resources | null, curr: Resources) {
  if (!prev) return;
  const names: Record<string, string> = { money: "$", iron: "Fe", steel: "Fe", fuel: "⛽", composites: "Cp", electronics: "⚡", munitions: "💣", uranium: "☢️", intelData: "📡", rareEarth: "💎" };
  const colors: Record<string, string> = { money: "#ffb77d", steel: "#98cdf2", fuel: "#f4a261", composites: "#8b6914", electronics: "#6fd8c8", munitions: "#e63946", uranium: "#00ff88", intelData: "#6a4c93", rareEarth: "#ff00ff" };
  let i = 0;
  for (const [key, val] of Object.entries(curr)) { const d = Math.floor(val) - Math.floor((prev as any)[key] || 0); if (d !== 0) { showFloatingNumber((d > 0 ? "+" : "") + d + " " + (names[key] || key), d > 0 ? (colors[key] || "#0f0") : "#e63946", 20 + i * 35, 55); i++; } }
}

export function App() {
  // Auth
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [authName, setAuthName] = useState("");
  const [authInvite, setAuthInvite] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authError, setAuthError] = useState("");
  // Game state
  (window as any).__BUILDING_CONFIGS = BUILDING_CONFIGS;
  const [tiles, setTiles] = useState<HexTile[]>([]);
  const [connected, setConnected] = useState(false);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [player, setPlayer] = useState<PlayerFull | null>(null);
  const [otherPlayers, setOtherPlayers] = useState<PlayerInfo[]>([]);
  const [selectedTile, setSelectedTile] = useState<(HexTile & { q: number; r: number }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // Panels
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [closingTab, setClosingTab] = useState<string | null>(null);
  const [showBuildMenu, setShowBuildMenu] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [offlineSummary, setOfflineSummary] = useState<any>(null);
  const [notifStatus, setNotifStatus] = useState<string>("checking");
  useEffect(() => {
    (async () => {
      try {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) { setNotifStatus("unavailable"); return; }
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setNotifStatus(sub ? "subscribed" : "not-setup");
      } catch { setNotifStatus("not-setup"); }
    })();
  }, []);
  const [showMissileMenu, setShowMissileMenu] = useState<{q:number,r:number}|null>(null);
  // Income
  const [income, setIncome] = useState<Record<string, number>>({});
  const [breakdown, setBreakdown] = useState<any[]>([]);
  const [attrition, setAttrition] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState<string | null>(null);
  const [resourceDrawer, setResourceDrawer] = useState(false);
  const prevResourcesRef = useRef<Resources | null>(null);
  // Diplomacy
  const [playerRelations, setPlayerRelations] = useState<Record<string, any>>({});
  const [pendingProposals, setPendingProposals] = useState<any[]>([]);
  
  const blockCanvasRef = useRef(false);
  const [commanders, setCommanders] = useState<any[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<any>(null);
  const [radialMenu, setRadialMenu] = useState<any>(null);
  const [missilePicker, setMissilePicker] = useState<any>(null);
  blockCanvasRef.current = !!pendingConfirm || !!radialMenu || !!missilePicker;
  (window as any).__ironpactBlockCanvas = blockCanvasRef;


  (window as any).__ironpactLongPress = (q: number, r: number, tile: any, screenX: number, screenY: number) => {
    if (blockCanvasRef.current) return;
    haptic("medium");
    sfxTap();
    setRadialMenu({ q, r, tile, x: screenX, y: screenY });
  };
  // Animations use global arrays — no React state to avoid re-renders
  const [showRecruitMenu, setShowRecruitMenu] = useState(false);
  const [diploView, setDiploView] = useState<"players" | "government" | "leaderboard">("players");
  // Stance
  const [myStance, setMyStance] = useState<GovernmentStance | null>(null);
  const [politicalWill, setPoliticalWill] = useState(10);
  const [stanceChangedAt, setStanceChangedAt] = useState(0);
  // Stability
  const [myStability, setMyStability] = useState(70);
  // Chat
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatTarget, setChatTarget] = useState<string | null>(null);
  const [unreadChat, setUnreadChat] = useState(0);
  // Tech
  const [researchedTechs, setResearchedTechs] = useState<string[]>([]);
  const [researchingTech, setResearchingTech] = useState<string | null>(null);
  const [researchEndsAt, setResearchEndsAt] = useState(0);
  // Trade
  const [trades, setTrades] = useState<any[]>([]);
  // Events / Intel
  const [eventFeed, setEventFeed] = useState<any[]>([]);
  const [unreadIntel, setUnreadIntel] = useState(0);
  // Missiles & Targeting
  const [activeMissiles, setActiveMissiles] = useState<any[]>([]);
  const [showTutorial, setShowTutorial] = useState(() => !localStorage.getItem("ironpact_tutorial_done"));
  const [targetingMode, setTargetingMode] = useState<string | null>(null);
  const [moveFrom, setMoveFrom] = useState<{ q: number; r: number; count: number } | null>(null);
  // Silos & Training & Timers
  const [siloInventory, setSiloInventory] = useState<Record<string, string[]>>({});
  const [batteryAmmo, setBatteryAmmo] = useState<Record<string, { count: number; max: number }>>({});
  const [batteryBuilding, setBatteryBuilding] = useState<Record<string, number>>({}); // batteryKey -> completesAt
  const [spyPicker, setSpyPicker] = useState<{ spyId: string; mission: string } | null>(null);
  const [trainingHexes, setTrainingHexes] = useState<Record<string, { count: number; completesAt: number; perTroopMs: number }>>({});

  // Expose building-in-progress hexes to Pixi for progress arcs
  useEffect(() => {
    const buildingHexes: any[] = [];
    for (const t of tiles) {
      if (!t.buildings) continue;
      for (const b of t.buildings) {
        if (b.completedAt && b.completedAt > Date.now()) {
          buildingHexes.push({ q: t.q, r: t.r, startedAt: b.startedAt || (Date.now() - 1000), completesAt: b.completedAt });
        }
      }
    }
    for (const [key, th] of Object.entries(trainingHexes)) {
      if ((th as any).completesAt > Date.now()) {
        const [q, r] = key.split(",").map(Number);
        buildingHexes.push({ q, r, startedAt: (th as any).completesAt - ((th as any).perTroopMs * (th as any).count), completesAt: (th as any).completesAt });
      }
    }
    (window as any).__ironpactBuildingHexes = buildingHexes;
  }, [tiles, trainingHexes]);
  const [activeTimers, setActiveTimers] = useState<any[]>([]);
  // Surveillance
  const [surveillanceData, setSurveillanceData] = useState<any[] | null>(null);
  // World Projects, Chronicle, Milestones
  const [worldProjects, setWorldProjects] = useState<any[]>([]);
  const [chronicle, setChronicle] = useState<any[]>([]);
  const [milestones, setMilestones] = useState<Record<string, string>>({});
  // Spies, Effects, Missed Events
  const [mySpies, setMySpies] = useState<any[]>([]);
  const [activeEffects, setActiveEffects] = useState<any[]>([]);
  const [missedEvents, setMissedEvents] = useState<any[] | null>(null);

  const playerColors: Record<string, string> = {};
  if (player) playerColors[player.id] = player.color;
  for (const p of otherPlayers) playerColors[p.id] = p.color;

  const socketRef = useRef<Socket | null>(null);
  const playerIdRef = useRef<string | null>(null);
  const selectedTileRef = useRef(selectedTile);
  playerIdRef.current = playerId;
  selectedTileRef.current = selectedTile;

  useEffect(() => { const i = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(i); }, []);

  // PWA install prompt
  useEffect(() => {
    const handler = (e: any) => { e.preventDefault(); setInstallPrompt(e); setShowInstallBanner(true); };
    window.addEventListener("beforeinstallprompt", handler);
    // Check if already installed (standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches) setShowInstallBanner(false);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // === AUTH ===
  useEffect(() => {
    let authed = false;
    const getToken = () => {
      const ls = localStorage.getItem("ironpact_token");
      if (ls) return "?token=" + ls;
      const ck = document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith("ironpact_token="));
      if (ck) return "?token=" + ck.split("=")[1];
      return "";
    };
    const tryAuth = (attempt: number) => {
      if (authed) return;
      fetch("/api/auth/me" + getToken(), { credentials: "include" })
        .then(r => { if (!r.ok) throw new Error("not authed"); return r.json(); })
        .then(d => {
          if (authed) return;
          if (d.playerId) {
            authed = true;
            setAuthState("game"); setPlayerId(d.playerId); (window as any).__ironpactPlayerId = d.playerId;
            if (d.token) localStorage.setItem("ironpact_token", d.token);
            setAuthName(d.name);
          } else if (attempt < 3) {
            setTimeout(() => tryAuth(attempt + 1), 1500);
          } else {
            setAuthState("login");
          }
        })
        .catch(() => {
          if (attempt < 3) setTimeout(() => tryAuth(attempt + 1), 1500);
          else setAuthState("login");
        });
    };
    tryAuth(0);
  }, []);

  const handleAuth = async (action: "login" | "register") => {
    setAuthError("");
    try {
      const r = await fetch(`/api/auth/${action}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: authName, passphrase: authPass, ...(authState === "register" ? { inviteCode: authInvite } : {}) }) });
      const d = await r.json();
      if (d.playerId || d.ok) { setPlayerId(d.playerId); (window as any).__ironpactPlayerId = d.playerId; setAuthState("game"); if (d.token) localStorage.setItem("ironpact_token", d.token); } else setAuthError(d.error || "Failed");
    } catch { setAuthError("Connection error"); }
  };

  // === SOCKET ===
  useEffect(() => {
    if (authState !== "game") return;
    const socket = io({ withCredentials: true, transports: ["websocket"] });

    // Auto-reconnect on visibility change (screen on/off)
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && !socket.connected) {
        console.log("[SOCKET] Reconnecting after visibility change...");
        socket.connect();
      }
    });
    socketRef.current = socket; (window as any).__ironpactSocket = socket;
    socket.on("connect", () => {
      setConnected(true);
      // Re-join game on reconnect (after screen off, background, etc)
      if (playerIdRef.current) {
        console.log("[SOCKET] Reconnected — re-joining game");
        socket.emit(SOCKET_EVENTS.JOIN_GAME, { playerId: playerIdRef.current });
      }
    });
    socket.on("disconnect", () => setConnected(false));

    socket.on(SOCKET_EVENTS.GAME_STATE, (data: any) => {
      setTiles(data.tiles || []);
      if (!(window as any).__chatLoaded) { socket.emit("chat-history"); socket.emit("event-history"); (window as any).__chatLoaded = true; }
      setPlayerId(data.playerId);
      setOtherPlayers(data.players.filter((p: any) => p.id !== data.playerId));
      if (data.player) {
        setPlayer(data.player); setResearchedTechs(data.player.techResearched || []);
        setMyStance(data.player.stance || null); setPoliticalWill(data.player.politicalWill ?? 10);
        setStanceChangedAt(data.player.stanceChangedAt || 0); setMyStability(data.player.stability ?? 70);
        setMySpies(data.player.spies || []);
      }
      if (data.income) setIncome(data.income);
      if (data.breakdown) setBreakdown(data.breakdown);
      if (data.attrition !== undefined) setAttrition(data.attrition);
      if (data.siloInventory) setSiloInventory(data.siloInventory);
      if (data.offlineSummary) setOfflineSummary(data.offlineSummary);
      if (data.missedEvents?.length > 0) setMissedEvents(data.missedEvents);
      if (data.worldProjects) setWorldProjects(data.worldProjects);
      if (data.chronicle) setChronicle(data.chronicle);
      if (data.milestones) setMilestones(data.milestones);
      if (data.activeWorldEffects) setActiveEffects(data.activeWorldEffects);
      // Request silos after game state loaded (socket now registered)
      if (data.myCommanders) setCommanders(data.myCommanders);
      socket.emit("get-all-silos"); socket.emit("get-all-batteries");
    });

    socket.on(SOCKET_EVENTS.STATE_DELTA, (delta: any) => {
      if (delta.hexUpdates) {
        setTiles(prev => { const map = new Map(prev.map(t => [`${t.q},${t.r}`, t])); for (const t of delta.hexUpdates) map.set(`${t.q},${t.r}`, t); return Array.from(map.values()); });
        // Update selected tile if it was in the delta
        const sel = selectedTileRef.current;
        if (sel) {
          const updated = delta.hexUpdates.find((t: any) => t.q === sel.q && t.r === sel.r);
          if (updated) setSelectedTile({ ...updated, q: sel.q, r: sel.r });
        }
      }
      if (delta.playerUpdates) {
        for (const pu of delta.playerUpdates) {
          if (pu.id === playerIdRef.current) {
            setPlayer(prev => {
              if (!prev) return prev;
              const u = { ...prev, ...pu };
              if (pu.resources) { checkResourceChanges(prevResourcesRef.current, pu.resources); prevResourcesRef.current = { ...pu.resources }; }
              if (pu.techResearched) setResearchedTechs(pu.techResearched);
              if (pu.stance !== undefined) setMyStance(pu.stance);
              if (pu.politicalWill !== undefined) setPoliticalWill(pu.politicalWill);
              if (pu.stability !== undefined) setMyStability(pu.stability);
              if (pu.spies) setMySpies(pu.spies);
              return u;
            });
          }
        }
      }
      if (delta.income) setIncome(delta.income);
      if (delta.breakdown) setBreakdown(delta.breakdown);
      if (delta.attrition !== undefined) setAttrition(delta.attrition);
    });

    socket.on("error", (d: any) => { sfxError(); showToast(d.message, "danger", 3000); });
    socket.on("error-msg", (d: any) => showToast(d.message, "danger"));
    socket.on("chat-message", (msg: any) => { sfxChat(); setChatMessages(prev => [...prev.slice(-99), msg]); setUnreadChat(c => c + 1); });
    socket.on("chat-history", (data: any) => setChatMessages(data.messages || data || []));
    socket.on("event-history", (data: any) => { setEventFeed(Array.isArray(data) ? data : data.events || []); });
    socket.on("relations-update", (d: any) => { setPlayerRelations(d.relations || {}); setPendingProposals(d.proposals || []); });
    socket.on("diplomacy-proposal", (d: any) => { showToast(d.fromName + " sent a proposal!", "epic"); setPendingProposals(prev => [...prev, d.proposal]); });
    socket.on("stance-update", (d: any) => { if (d.stance !== undefined) setMyStance(d.stance); if (d.politicalWill !== undefined) setPoliticalWill(d.politicalWill); if (d.stanceChangedAt !== undefined) setStanceChangedAt(d.stanceChangedAt); });
    socket.on("event-feed", (event: any) => {
      if (event.type === "war" || event.type === "battle") haptic("heavy");
      else if (event.type === "alliance" || event.type === "milestone") haptic("success");
      setEventFeed(prev => [...prev.slice(-49), { ...event, time: event.time || Date.now() }]);
    });
    socket.on("GAME_EVENT", (event: any) => {
      // Normalize timestamp field
      if (!event.time && event.timestamp) event.time = event.timestamp;
      if (!event.time) event.time = Date.now();
      setEventFeed(prev => [...prev.slice(-49), event]);
      const important = ["build", "missile", "nuclear", "war", "battle", "diplomacy", "milestone", "wonder", "spy_success", "world_event", "cyber_attack"];
      if (important.includes(event.type)) setUnreadIntel(c => c + 1);
      if (event.type === "nuclear") { screenShake(true); screenFlash("white"); }
      else if (event.type === "missile") { screenShake(false); screenFlash("red"); }
      else if (event.type === "war" || event.type === "cyber_attack") screenFlash("red");
      else if (event.type === "milestone" || event.type === "world_event") screenFlash("gold");
      if (event.type === "nuclear" || event.type === "missile") showToast(event.message, "danger", 5000);
      else if (event.type === "war") showToast(event.message, "danger", 4000);
      else if (event.type === "milestone") showToast(event.message, "epic", 5000);
      else if (event.type === "build") showToast(event.message, "success", 2500);
    });
    socket.on("missile-building", (d: any) => {
      setActiveTimers(prev => [...prev, { type: "missile", ...d }]);
      showToast(`Building ${d.missileType} missile — ${Math.ceil((d.completesAt - Date.now()) / 1000)}s`, "default", Math.min(5000, d.completesAt - Date.now()));
    });
    socket.on("missile-alert", () => { sfxIncoming(); showToast("INCOMING MISSILE!", "danger", 5000); });
    socket.on("launch-confirmed", (d: any) => {
      setTargetingMode(null);
      // Animation handled by missile-outgoing event — no duplicate here
    });
    socket.on("missile-incoming", (d: any) => {
      haptic("heavy");
      if (d.targetHex && d.eta) {
        const fromQ = d.fromHex?.q ?? d.targetHex.q;
        const fromR = d.fromHex?.r ?? d.targetHex.r;
        const incNow = Date.now();
        const incFlightMs = d.eta - incNow;
        const incClientEta = incNow + incFlightMs;
        setActiveMissiles(prev => [...prev, {
          id: `inc_${incNow}`,
          from: { q: fromQ, r: fromR },
          to: { q: d.targetHex.q, r: d.targetHex.r },
          type: d.type || d.missileType || "unknown", launchedAt: incNow, eta: incClientEta,
          willIntercept: d.willIntercept || false,
          interceptorHex: d.interceptorHex,
        }]);
        // If intercept, schedule counter-missile animation at 60% flight time
        if (d.hasBattery && d.interceptorHex && d.counterTo) {
          const counterLaunch = incNow + incFlightMs * 0.3;
          const interceptTime = incNow + incFlightMs * 0.65;
          const counterEta = d.willIntercept ? interceptTime : interceptTime + incFlightMs * 0.05;
          // Create immediately with future launchedAt — HexMap skips until now >= launchedAt
          setActiveMissiles(prev => [...prev, {
            id: d.willIntercept ? `counter_inc_${incNow}` : `counter_miss_inc_${incNow}`,
            from: { q: d.interceptorHex.q, r: d.interceptorHex.r },
            to: { q: d.counterTo.q, r: d.counterTo.r },
            targetMissileId: d.willIntercept ? `inc_${incNow}` : undefined,
            type: "counter", launchedAt: counterLaunch, eta: counterEta,
            isCounter: true,
          }]);
          if (d.willIntercept) {
            // Kill incoming missile at intercept time
            setTimeout(() => {
              setActiveMissiles(prev => prev.filter(m => m.id !== `inc_${incNow}`));
            }, incFlightMs * 0.65 + 50);
          }
        }
      }
      const sirenMs = d.eta - Date.now(); sfxIncoming(Math.max(5000, sirenMs)); showToast("⚠️ MISSILE INCOMING!" + (d.fromHex ? ` Origin detected: (${d.fromHex.q},${d.fromHex.r})` : " Origin unknown"), "danger", 4000);
    });

    // Attacker sees their own missile flight + intercept
    socket.on("missile-outgoing", (d: any) => {
      const fromQ = d.fromHex?.q ?? 0;
      const fromR = d.fromHex?.r ?? 0;
      const now = Date.now();
      const totalFlightMs = d.eta - now;
      const clientEta = now + totalFlightMs; // All client-relative
      setActiveMissiles(prev => [...prev, {
        id: `out_${now}`,
        from: { q: fromQ, r: fromR },
        to: { q: d.targetHex.q, r: d.targetHex.r },
        type: d.type || "tactical", launchedAt: now, eta: clientEta,
      }]);
      if (d.hasBattery && d.interceptorHex && d.counterTo) {
        const counterLaunch = now + totalFlightMs * 0.3;
        const interceptTime = now + totalFlightMs * 0.65;
        const counterEta = d.willIntercept ? interceptTime : interceptTime + totalFlightMs * 0.05;
        setActiveMissiles(prev => [...prev, {
          id: d.willIntercept ? `counter_out_${now}` : `counter_miss_out_${now}`,
          from: { q: d.interceptorHex.q, r: d.interceptorHex.r },
          to: { q: d.counterTo.q, r: d.counterTo.r },
          targetMissileId: d.willIntercept ? `out_${now}` : undefined,
          type: "counter", launchedAt: counterLaunch, eta: counterEta,
          isCounter: true,
        }]);
        if (d.willIntercept) {
          setTimeout(() => {
            setActiveMissiles(prev => prev.filter(m => m.id !== `out_${now}`));
          }, totalFlightMs * 0.65 + 50);
        }
      }
      showToast("🚀 Missile launched!", "info", 3000);
    });
    // Safety: clear stale missiles every 5s
    const missileCleanup = setInterval(() => {
      setActiveMissiles(prev => {
        const now = Date.now();
        const fresh = prev.filter(m => now - m.eta < 10000); // keep missiles only 10s past eta
        return fresh.length !== prev.length ? fresh : prev;
      });
    }, 5000);

    socket.on("missile-impact", (d: any) => { stopSiren(); sfxMissileImpact();
      haptic("error");
      const tq = d.targetHex?.q ?? d.targetQ ?? "?";
      const tr = d.targetHex?.r ?? d.targetR ?? "?";
      const mtype = (d.type || d.missileType || "unknown").toUpperCase();
      const msg = d.intercepted ? `Missile INTERCEPTED at (${tq},${tr})!` : `${mtype} IMPACT at (${tq},${tr}) — ${d.damage || 0} damage!`;
      showToast(msg, d.intercepted ? "success" : "danger", 4000);
      if (d.intercepted) { screenFlash("green"); }
      else { screenShake(d.type === "nuclear" || d.missileType === "nuclear"); screenFlash(d.type === "nuclear" || d.missileType === "nuclear" ? "white" : "red"); }
      // Clean up finished animation after 2s
      const impQ = d.targetHex?.q ?? d.targetQ;
      const impR = d.targetHex?.r ?? d.targetR;
      setTimeout(() => {
        setActiveMissiles(prev => prev.filter(m => !(m.to.q === impQ && m.to.r === impR && m.eta <= Date.now())));
      }, 2000);
    });
    socket.on("silo-update", (d: any) => { if (d.inventory) setSiloInventory(prev => ({ ...prev, [d.siloKey]: d.inventory })); });
    socket.on("battery-update", (d: any) => { setBatteryAmmo(prev => ({ ...prev, [d.batteryKey]: { count: d.count, max: d.max } })); });
    socket.on("counter-missile-building", (d: any) => { setBatteryBuilding(prev => ({ ...prev, [d.batteryKey]: d.completesAt })); });
    socket.on("counter-missile-built", (d: any) => { setBatteryBuilding(prev => { const n = { ...prev }; delete n[d.batteryKey]; return n; }); });
    socket.on("all-silos", (d: any) => { if (d) setSiloInventory(d); });
    socket.on("all-batteries", (d: any) => { if (d) setBatteryAmmo(d); });
    socket.on("missile-built", (d: any) => {
      showToast(`${d.missileType} missile READY!`, "success", 3000);
      // Also request fresh silo inventory
      socket.emit("get-all-silos"); socket.emit("get-all-batteries");
    });
    socket.on("training-started", (d: any) => { const hq = d.hex?.q ?? d.q; const hr = d.hex?.r ?? d.r; setTrainingHexes(prev => ({ ...prev, [`${hq},${hr}`]: { count: d.count, completesAt: d.completesAt, perTroopMs: d.perTroopMs } })); });
    socket.on("research-started", (d: any) => { setResearchingTech(d.techId); setResearchEndsAt(d.completesAt); });
    socket.on("research-complete", (d: any) => { sfxResearch(); setResearchedTechs(prev => [...prev, d.techId]); setResearchingTech(null); showToast("Research complete: " + d.techId, "success"); });
    socket.on("surveillance-data", (d: any) => setSurveillanceData(d.players));
    socket.on("wonder-update", (d: any) => { if (d.projects) setWorldProjects(d.projects); });
    socket.on("chronicle-entry", (entry: any) => setChronicle(prev => [...prev.slice(-99), entry]));
    socket.on("chronicle-data", (d: any) => { if (d.entries) setChronicle(d.entries); if (d.milestones) setMilestones(d.milestones); });
    socket.on("spy-update", (d: any) => { if (d.spies) setMySpies(d.spies); });
    socket.on("world-effect-update", (d: any) => { if (d.effects) setActiveEffects(d.effects); });
    socket.on("trade-list-data", (d: any) => setTrades(d.trades || []));
    socket.on("trade-created", (t: any) => setTrades(prev => [...prev, t]));
    socket.on("trade-accepted", (d: any) => setTrades(prev => prev.filter(t => t.id !== d.tradeId)));
    
    socket.on("commander-recruited", (cmd: any) => {
      showToast("Recruited " + cmd.name + " (" + cmd.specialty + ")", "epic", 4000);
    });
    socket.on("commander-updated", (d: any) => setCommanders(d.commanders || []));
    socket.on("commander-data", (d: any) => setCommanders(d.commanders || []));
    socket.on("leaderboard-data", (d: any) => setLeaderboard(d.rankings || []));
    socket.on("trade-cancelled", (d: any) => setTrades(prev => prev.filter(t => t.id !== d.tradeId)));

    socket.emit(SOCKET_EVENTS.JOIN_GAME, { playerId: playerIdRef.current });
  
  // Push notification subscription
  async function subscribePush(pid: string) {
    try {
      if (!("serviceWorker" in navigator)) { showToast("No Service Worker", "danger"); return; }
      if (!("PushManager" in window)) { showToast("No PushManager - install as PWA first", "danger"); return; }
      if (!("Notification" in window)) { showToast("No Notification API", "danger"); return; }
      showToast("Requesting permission...", "default");
      const reg = await navigator.serviceWorker.ready;
      const vapidRes = await fetch("/api/push/vapid-key");
      const { publicKey } = await vapidRes.json();
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { showToast("Permission: " + permission, "danger"); setNotifStatus("denied"); return; }
      showToast("Subscribing...", "default");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const key = Uint8Array.from(atob(publicKey.replace(/-/g,"+").replace(/_/g,"/")), c => c.charCodeAt(0));
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      }
      await fetch("/api/push/subscribe", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ playerId: pid, subscription: sub.toJSON() }) });
      showToast("Push enabled!", "success");
      setNotifStatus("subscribed");
    } catch (e: any) { showToast("Failed: " + (e.message || e), "danger"); setNotifStatus("failed"); }
  }

  return () => { socket.disconnect(); };
  }, [authState]);

  // === HEX CLICK ===
  const handleHexClick = useCallback((q: number, r: number, tile: HexTile | undefined) => {
        if (targetingMode?.startsWith("missile:")) { const [, type, from] = targetingMode.split(":"); const [fq, fr] = from.split(",").map(Number); setPendingConfirm({ action: "missile", type, siloQ: fq, siloR: fr, targetQ: q, targetR: r }); (window as any).__ironpactClearSelection?.(); return; }
    if (targetingMode === "move" && moveFrom) { optimisticMove(moveFrom.q, moveFrom.r, q, r, moveFrom.count); socketRef.current?.emit(SOCKET_EVENTS.MOVE_TROOPS, { fromQ: moveFrom.q, fromR: moveFrom.r, toQ: q, toR: r, count: moveFrom.count }); setTargetingMode(null); setMoveFrom(null); (window as any).__ironpactClearSelection?.(); return; }
    if (targetingMode?.startsWith("commander:")) { const cmdId = targetingMode.split(":")[1]; haptic("medium"); sfxClaim(); socketRef.current?.emit("assign-commander", { commanderId: cmdId, q, r }); setTargetingMode(null); (window as any).__ironpactClearSelection?.(); return; }
    setShowBuildMenu(false);
    if (selectedTileRef.current?.q === q && selectedTileRef.current?.r === r) { setSelectedTile(null); (window as any).__selectedHex = null; return; }
    setSelectedTile(tile ? { ...tile, q, r } : { q, r, biome: "ocean" as any, elevation: 0, moisture: 0 }); (window as any).__selectedHex = tile ? { ...tile, q, r } : null; haptic("light"); sfxTap();
  }, [targetingMode, moveFrom]);


  // === OPTIMISTIC UPDATES ===
  // Apply changes instantly, server reconciles via STATE_DELTA
  const optimisticClaim = (q: number, r: number) => {
    sfxClaim();
    // Visual feedback via Pixi animation only — no setTiles (avoids full redraw jitter)
    const pColor = playerColors?.[playerId || ""] || "#00ffaa";
    const anims = ((window as any).__ironpactAnims = (window as any).__ironpactAnims || { pulses: [], moves: [], claims: [] });
    anims.pulses.push({ q, r, color: parseInt(pColor.replace("#", ""), 16), time: Date.now() });
    anims.claims.push({ q, r, color: parseInt(pColor.replace("#", ""), 16), time: Date.now() });
  };

  const optimisticBuild = (q: number, r: number, buildingType: string) => {
    sfxBuild();
    // No setTiles — server STATE_DELTA handles real update
  };

  const optimisticTrain = (q: number, r: number, count: number) => {
    sfxTrain();
    // No setTiles — server STATE_DELTA handles real update
  };

  const optimisticMove = (fromQ: number, fromR: number, toQ: number, toR: number, count: number) => {
    sfxTap();
    // Move line animation only — no setTiles
    const anims = ((window as any).__ironpactAnims = (window as any).__ironpactAnims || { pulses: [], moves: [], claims: [] });
    anims.moves.push({ fromQ, fromR, toQ, toR, time: Date.now() });
  };

  const switchTab = (tab: string) => {
    if (activeTab === tab) { setClosingTab(tab); setTimeout(() => { setActiveTab(null); setClosingTab(null); }, 200); }
    else { setActiveTab(tab); setClosingTab(null); if (tab === "chat") setUnreadChat(0); if (tab === "events") { setUnreadIntel(0); } if (tab === "diplomacy") { socketRef.current?.emit("get-relations"); socketRef.current?.emit("get-leaderboard"); }; if (tab === "trade") socketRef.current?.emit("trade-list"); if (tab === "commanders") socketRef.current?.emit("get-commanders"); if (tab === "market") { socketRef.current?.emit("market-summary"); socketRef.current?.emit("market-my-orders"); } }
  };

  const now = Date.now();

  // === AUTH SCREENS ===
  if (authState === "checking") return <div className="bg-background h-[100dvh] flex items-center justify-center"><span className="font-headline text-2xl text-primary-container tracking-widest animate-pulse">IRONPACT</span></div>;

  if (authState === "login" || authState === "register") return (
    <div className="bg-background h-[100dvh] flex flex-col items-center justify-center gap-4 px-8">
      <h1 className="font-headline text-3xl text-primary-container tracking-tighter mb-4">IRONPACT</h1>
      <input value={authName} onChange={e => setAuthName(e.target.value)} placeholder="Commander Name"
        className="bg-surface-container border border-outline-variant/20 rounded px-4 py-3 text-on-surface font-label text-sm w-64 focus:border-primary-container/50 outline-none" />
      <input value={authPass} onChange={e => setAuthPass(e.target.value)} placeholder="Passphrase" type="password"
        className="bg-surface-container border border-outline-variant/20 rounded px-4 py-3 text-on-surface font-label text-sm w-64 focus:border-primary-container/50 outline-none" />
      {authState === "register" && <input value={authInvite} onChange={e => setAuthInvite(e.target.value.toUpperCase())} placeholder="Invite Code (e.g. IP-ABC123)"
        className="bg-surface-container border border-outline-variant/20 rounded px-4 py-3 text-on-surface font-label text-sm w-64 focus:border-primary-container/50 outline-none tracking-widest font-headline" />}
      {authError && <p className="text-error text-xs font-label">{authError}</p>}
      <button onClick={() => handleAuth(authState)}
        className="w-64 py-3 border border-primary-container text-primary-container font-headline text-xs tracking-[0.2em] uppercase hover:bg-primary-container/10 active:scale-95 transition-all">
        {authState === "login" ? "AUTHENTICATE" : "REGISTER_NEW"}
      </button>
      <p onClick={() => setAuthState(authState === "login" ? "register" : "login")}
        className="text-xs font-label text-zinc-500 cursor-pointer hover:text-primary-container transition-colors">
        {authState === "login" ? "New commander? Register" : "Existing? Login"}
      </p>
    </div>
  );

  // === GAME UI ===

  // Push notification subscription

  return (
    <div onTouchStart={unlockAudio} onClick={unlockAudio} className="ip-game-ui w-full h-[100dvh] bg-background relative overflow-hidden">
      <div className="ip-top-safe" />
      {/* Missed events overlay */}
      {offlineSummary && (
        <div className="fixed inset-0 bg-black/90 z-[10000] flex items-center justify-center p-4" onClick={() => setOfflineSummary(null)}>
          <div className="bg-surface-container border border-white/10 rounded-2xl max-w-sm w-full p-5 max-h-[80vh] overflow-y-auto" onClick={(e: any) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <span className="material-symbols-outlined text-4xl text-amber-400">schedule</span>
              <h2 className="font-headline text-lg text-on-surface mt-1">WHILE YOU WERE AWAY</h2>
              <p className="text-on-surface-variant text-xs">
                {offlineSummary.offlineMinutes < 60 
                  ? offlineSummary.offlineMinutes + " minutes offline"
                  : offlineSummary.offlineMinutes < 1440
                    ? Math.floor(offlineSummary.offlineMinutes / 60) + "h " + (offlineSummary.offlineMinutes % 60) + "m offline"
                    : Math.floor(offlineSummary.offlineMinutes / 1440) + "d " + Math.floor((offlineSummary.offlineMinutes % 1440) / 60) + "h offline"}
              </p>
            </div>
            {Object.keys(offlineSummary.resourcesEarned || {}).length > 0 && (
              <div className="mb-3 p-3 bg-green-900/20 border border-green-500/20 rounded-lg">
                <div className="font-headline text-[10px] text-green-400 uppercase mb-2 tracking-wider">💰 Resources Earned</div>
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries(offlineSummary.resourcesEarned).map(([key, val]: [string, any]) => {
                    const n: Record<string, string> = { money: "$", steel: "Fe", fuel: "Fuel", composites: "Cp", electronics: "Elec", munitions: "Mun", uranium: "U", intelData: "Intel", rareEarth: "RE" };
                    return <div key={key} className="text-xs text-on-surface"><span className="text-green-400">+{Math.floor(val)}</span> {n[key] || key}</div>;
                  })}
                </div>
              </div>
            )}
            {offlineSummary.buildingsCompleted > 0 && (
              <div className="mb-3 p-3 bg-amber-900/20 border border-amber-500/20 rounded-lg">
                <div className="font-headline text-[10px] text-amber-400 uppercase tracking-wider">🏗️ {offlineSummary.buildingsCompleted} building{offlineSummary.buildingsCompleted > 1 ? "s" : ""} completed</div>
              </div>
            )}
            <div className="mb-3 p-3 bg-surface-container-highest rounded-lg">
              <div className="font-headline text-[10px] text-on-surface-variant uppercase tracking-wider mb-1">Empire Status</div>
              <div className="text-xs text-on-surface">🏰 {offlineSummary.territoryCount} hexes controlled</div>
              <div className="text-xs text-on-surface">📋 {offlineSummary.eventsCount} events while away</div>
            </div>
            <button onClick={() => setOfflineSummary(null)}
              className="w-full py-2.5 bg-primary-container text-on-primary-container font-headline text-sm uppercase rounded-lg active:scale-95 transition-all">
              RETURN TO COMMAND
            </button>
          </div>
        </div>
      )}

      {/* Install Banner */}
      {showInstallBanner && (
        <div className="fixed top-11 left-0 w-full z-[100] px-3 py-2 bg-primary-container/95 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-background text-sm">download</span>
            <span className="font-headline text-[10px] text-background uppercase tracking-wider">Install IRONPACT for the best experience</span>
          </div>
          <div className="flex gap-2">
            <button onClick={async () => { if (installPrompt) { installPrompt.prompt(); const r = await installPrompt.userChoice; if (r.outcome === "accepted") setShowInstallBanner(false); } }}
              className="px-3 py-1 bg-background text-primary-container font-headline text-[9px] uppercase active:scale-95">INSTALL</button>
            <button onClick={() => setShowInstallBanner(false)}
              className="text-background/60 font-headline text-[9px]">✕</button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <header className="ip-header fixed w-full h-10 border-b border-orange-500/20 bg-background/95 backdrop-blur-xl flex items-center justify-between px-3 z-50 shadow-[0_0_15px_rgba(255,140,0,0.1)]">
        <div className="flex items-center gap-2">
          <MS icon="satellite_alt" className="text-orange-500 text-sm" />
          <span className="font-label uppercase tracking-[0.1em] text-[10px] text-orange-500 font-black">IRONPACT_OS</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-orange-500 animate-pulse" : "bg-zinc-600"}`} />
          <span className="font-headline text-[9px] text-orange-500/60">{connected ? "UPLINK" : "OFFLINE"}</span>
        </div>
      </header>

      {/* Resource bar - single compact block */}
      {player && (
        <div className="ip-below-header fixed left-0 w-full z-40 glass-panel border-b border-orange-500/20 shadow-[0_2px_8px_rgba(255,106,0,0.08)]">
          {/* Tappable resource rows */}
          <div className="cursor-pointer active:brightness-110" onClick={() => { setResourceDrawer(!resourceDrawer); if (resourceDrawer) setShowBreakdown(null); }}>
            <div className="flex items-center px-2 pt-[3px]">
              {[
                { key: "money", icon: "💰" },
                { key: "steel", icon: "⚙️" },
                { key: "fuel", icon: "⛽" },
              ].map(({ key, icon }) => {
                const val = Math.floor((player.resources as any)[key] || 0);
                const fmt = val >= 1000000 ? (val / 1000000).toFixed(1) + "M" : val >= 10000 ? Math.floor(val / 1000) + "K" : val >= 1000 ? (val / 1000).toFixed(1) + "K" : String(val);
                return (
                  <div key={key} className="flex-1 flex items-center justify-center gap-[2px]">
                    <span className="text-[10px] leading-none">{icon}</span>
                    <span className="font-headline text-[10px] font-bold text-on-surface leading-none">{fmt}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center px-2 pb-[2px]">
              {[
                { key: "electronics", icon: "⚡" },
                { key: "munitions", icon: "💣" },
              ].map(({ key, icon }) => {
                const val = Math.floor((player.resources as any)[key] || 0);
                const fmt = val >= 1000000 ? (val / 1000000).toFixed(1) + "M" : val >= 10000 ? Math.floor(val / 1000) + "K" : val >= 1000 ? (val / 1000).toFixed(1) + "K" : String(val);
                return (
                  <div key={key} className="flex-1 flex items-center justify-center gap-[2px]">
                    <span className="text-[10px] leading-none">{icon}</span>
                    <span className="font-headline text-[10px] font-bold text-on-surface leading-none">{fmt}</span>
                  </div>
                );
              })}
              <div className="flex-1 flex items-center justify-center">
                <span className="text-[10px] text-zinc-500">{resourceDrawer ? "▲" : "▼"}</span>
              </div>
            </div>
          </div>
          {/* Stability + War Strain thin bars */}
          <div className="flex gap-2 px-2 pb-1">
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <span className="text-[6px] font-label text-zinc-500 uppercase">STB</span>
                <span className={`text-[6px] font-headline ${myStability >= 80 ? "text-status-allied" : myStability >= 50 ? "text-secondary" : myStability >= 30 ? "text-primary-container" : "text-error"}`}>{Math.floor(myStability)}%</span>
              </div>
              <div className="w-full h-[3px] bg-zinc-800 rounded-full"><div className={`h-full rounded-full ${myStability >= 80 ? "bg-status-allied" : myStability >= 50 ? "bg-secondary" : myStability >= 30 ? "bg-primary-container" : "bg-error"}`} style={{ width: myStability + "%" }} /></div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <span className="text-[6px] font-label text-zinc-500 uppercase">WAR</span>
                <span className="text-[6px] font-headline text-error">{Math.round((window as any).__myWarStrain || 0)}%</span>
              </div>
              <div className="w-full h-[3px] bg-zinc-800 rounded-full"><div className="h-full rounded-full bg-error/50" style={{ width: ((window as any).__myWarStrain || 0) + "%" }} /></div>
            </div>
          </div>
        </div>
      )}

      {/* Full resource overlay */}
      {resourceDrawer && (
        <div className="fixed inset-0 z-[45]" onClick={() => { setResourceDrawer(false); setShowBreakdown(null); }}>
          <div className="mt-[90px] mx-3 glass-panel border border-white/10 rounded-lg p-3 shadow-2xl" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-2">
              <span className="font-headline text-[10px] text-zinc-400 uppercase tracking-wider">All Resources</span>
              <button onClick={() => { setResourceDrawer(false); setShowBreakdown(null); }} className="text-zinc-500 text-[14px] active:text-white">✕</button>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { key: "money", name: "Money", icon: "💰" },
                { key: "steel", name: "Steel", icon: "⚙️" },
                { key: "fuel", name: "Fuel", icon: "⛽" },
                { key: "electronics", name: "Electronics", icon: "⚡" },
                { key: "munitions", name: "Munitions", icon: "💣" },
                { key: "composites", name: "Composites", icon: "🧪" },
                { key: "uranium", name: "Uranium", icon: "☢️" },
                { key: "rareEarth", name: "Rare Earth", icon: "💎" },
                { key: "intelData", name: "Intel", icon: "📡" },
              ].map(({ key, name, icon }) => {
                const val = Math.floor((player.resources as any)[key] || 0);
                const fmt = val >= 1000000 ? (val / 1000000).toFixed(1) + "M" : val >= 10000 ? Math.floor(val / 1000) + "K" : val >= 1000 ? (val / 1000).toFixed(1) + "K" : String(val);
                const inc = (income as any)[key] || 0;
                return (
                  <div key={key} onClick={() => setShowBreakdown(showBreakdown === key ? null : key)} className={`flex flex-col items-center p-1.5 rounded-lg border cursor-pointer active:bg-white/5 ${showBreakdown === key ? "border-orange-500/40 bg-white/5" : "border-white/5 bg-surface-container-low/30"}`}>
                    <span className="text-[16px]">{icon}</span>
                    <span className="text-[7px] font-headline text-zinc-500 uppercase">{name}</span>
                    <span className="font-headline text-[11px] font-bold text-on-surface">{fmt}</span>
                    {inc !== 0 && <span className={`text-[7px] font-headline ${inc > 0 ? "text-green-400" : "text-red-400"}`}>{inc > 0 ? "+" : ""}{Math.floor(inc)}/t</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Resource Breakdown Popup */}
      {showBreakdown && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16" onClick={() => setShowBreakdown(null)}>
          <div className="bg-surface-container border border-white/10 rounded-lg p-3 w-72 max-h-80 overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-2">
              <span className="font-headline text-[12px] text-on-surface uppercase">{showBreakdown} breakdown</span>
              <button onClick={() => setShowBreakdown(null)} className="text-zinc-500 text-[14px]">✕</button>
            </div>
            {(() => {
              const items = breakdown.filter((b: any) => b.resource === showBreakdown);
              const incomeItems = items.filter((b: any) => b.type === "income");
              const expenseItems = items.filter((b: any) => b.type === "expense");
              const totalIncome = incomeItems.reduce((s: number, b: any) => s + b.amount, 0);
              const totalExpense = expenseItems.reduce((s: number, b: any) => s + Math.abs(b.amount), 0);
              const net = totalIncome - totalExpense;
              return (<>
                {incomeItems.length > 0 && (<>
                  <p className="text-[9px] font-headline text-green-400 mb-1 uppercase">Income +{Math.round(totalIncome * 10) / 10}</p>
                  {incomeItems.map((b: any, i: number) => (
                    <div key={i} className="flex justify-between text-[8px] font-label text-zinc-400 ml-2">
                      <span>{b.source}</span>
                      <span className="text-green-400">+{Math.round(b.amount * 10) / 10}</span>
                    </div>
                  ))}
                </>)}
                {expenseItems.length > 0 && (<>
                  <p className="text-[9px] font-headline text-red-400 mt-2 mb-1 uppercase">Expenses -{Math.round(totalExpense * 10) / 10}</p>
                  {expenseItems.map((b: any, i: number) => (
                    <div key={i} className="flex justify-between text-[8px] font-label text-zinc-400 ml-2">
                      <span>{b.source}</span>
                      <span className="text-red-400">{Math.round(b.amount * 10) / 10}</span>
                    </div>
                  ))}
                </>)}
                <div className="border-t border-white/10 mt-2 pt-1 flex justify-between text-[10px] font-headline">
                  <span className="text-on-surface">NET</span>
                  <span className={net >= 0 ? "text-green-400" : "text-red-400"}>{net >= 0 ? "+" : ""}{Math.round(net * 10) / 10}/tick</span>
                </div>
                {attrition && showBreakdown === "fuel" && (
                  <p className="text-[9px] text-red-500 font-headline mt-2 animate-pulse">⚠️ ATTRITION: Troops at -50% effectiveness!</p>
                )}
              </>);
            })()}
          </div>
        </div>
      )}



      {/* Active effects */}
      {activeEffects.length > 0 && (
        <div className="fixed top-[5rem] left-0 w-full px-3 z-40 py-0.5 glass-panel border-b border-primary-container/10">
          {activeEffects.map((e: any, i: number) => (
            <span key={i} className="font-headline text-[8px] text-primary-container mr-3">{e.eventId} ({Math.max(0, Math.ceil((e.expiresAt - now) / 60000))}m)</span>
          ))}
        </div>
      )}

      {/* Targeting banner */}
      {targetingMode && (
        targetingMode.startsWith("commander:") ? <div className="fixed top-[7rem] left-0 right-0 z-50 text-center py-2 bg-green-900/90 text-green-300 font-headline text-[11px] tracking-wider">TAP A HEX TO DEPLOY COMMANDER <button onClick={() => setTargetingMode(null)} className="ml-3 text-red-400 underline">CANCEL</button></div> :
        <div className={`fixed top-[7rem] left-1/2 -translate-x-1/2 z-40 w-[90%] ${targetingMode === "move" ? "bg-secondary-container/90 border-secondary/50" : "bg-error-container/90 border-error/50"} border px-4 py-2 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <MS icon="target" className={`${"text-error"} animate-pulse text-sm`} />
            <span className="font-headline text-[10px] tracking-widest uppercase text-on-surface">
              {targetingMode === "move" ? "MOVE TROOPS (max 5 hexes)" : "TARGETING: MISSILE"}
            </span>
          </div>
          <button onClick={() => { setTargetingMode(null); setMoveFrom(null); }} className="text-[10px] font-headline text-on-surface border border-white/30 px-2 py-0.5 uppercase">Cancel</button>
        </div>
      )}
      {/* HEX MAP */}
      <HexMap tiles={tiles} onHexClick={handleHexClick} playerColors={playerColors} centerOn={player?.capitalHex} activeMissiles={activeMissiles} tick={tick} commanderHexes={commanders.filter((c: any) => c.assignedHex && c.status === "active").map((c: any) => ({ q: c.assignedHex.q, r: c.assignedHex.r, specialty: c.specialty, name: c.name }))} />

      {/* Tutorial overlay */}
      {showTutorial && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}>
          <div style={{background:"#1a2233",border:"1px solid #ff6a00",borderRadius:"12px",padding:"24px",maxWidth:"400px",width:"100%",maxHeight:"85vh",overflowY:"auto",color:"#e0e0e0",fontFamily:"system-ui"}}>
            <h2 style={{color:"#ff6a00",margin:"0 0 16px",fontSize:"20px",textAlign:"center"}}>⚔️ Welcome to IronPact</h2>
            <div style={{fontSize:"13px",lineHeight:"1.6"}}>
              <p style={{marginBottom:"12px"}}><b>🎯 Goal:</b> Build an empire, research tech, and dominate the map.</p>
              <p style={{color:"#ff6a00",fontWeight:"bold",marginBottom:"4px"}}>Getting Started:</p>
              <ul style={{paddingLeft:"16px",marginBottom:"12px"}}>
                <li><b>Tap a hex</b> next to your capital to claim it</li>
                <li><b>Build mines</b> on steel deposits, <b>oil wells</b> on fuel deposits</li>
                <li><b>Build a power plant</b> near production buildings (+50% boost)</li>
                <li><b>Research tech</b> in the Research tab to unlock buildings</li>
              </ul>
              <p style={{color:"#ff6a00",fontWeight:"bold",marginBottom:"4px"}}>Military:</p>
              <ul style={{paddingLeft:"16px",marginBottom:"12px"}}>
                <li><b>Barracks</b> let you train troops (tap hex → Train)</li>
                <li><b>Move troops</b> to attack enemy hexes</li>
                <li><b>Missile Silos</b> for long-range strikes (requires research)</li>
                <li><b>Anti-Missile Batteries</b> auto-intercept incoming missiles</li>
              </ul>
              <p style={{color:"#ff6a00",fontWeight:"bold",marginBottom:"4px"}}>Tips:</p>
              <ul style={{paddingLeft:"16px",marginBottom:"12px"}}>
                <li><b>Long-press</b> any hex for a quick action menu</li>
                <li><b>Tap resources</b> at the top to see income breakdown</li>
                <li>Claiming hexes gets more expensive as you expand</li>
                <li>Troops cost upkeep — don't recruit more than you can afford!</li>
              </ul>
            </div>
            <button onClick={() => { localStorage.setItem("ironpact_tutorial_done", "1"); setShowTutorial(false); }} style={{width:"100%",padding:"12px",background:"#ff6a00",color:"#000",border:"none",borderRadius:"8px",fontSize:"16px",fontWeight:"bold",cursor:"pointer",marginTop:"8px"}}>
              LET'S GO ⚔️
            </button>
          </div>
        </div>
      )}



      {/* Hex detail panel */}
      {selectedTile && !showBuildMenu && activeTab === null && (
        <div className="ip-slide-up fixed bottom-14 left-0 w-full z-40 glass-panel border-t border-orange-500/30 shadow-[0_-10px_30px_rgba(0,0,0,0.8)] px-4 pt-3 pb-4 max-h-[280px] overflow-y-auto">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h2 className="font-headline text-sm tracking-widest text-on-surface">
                ({selectedTile.q},{selectedTile.r}) <span className="text-zinc-500 font-body text-[10px]">[{selectedTile.biome?.toUpperCase()}]</span>
              </h2>
              {selectedTile.ownerId && (
                <p className="text-[10px] font-label tracking-wider" style={{ color: playerColors[selectedTile.ownerId] || "#888" }}>
                  OWNER: {selectedTile.ownerId === playerId ? "YOU" : otherPlayers.find(p => p.id === selectedTile.ownerId)?.name || "Unknown"}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedTile.resource && <span className="text-[10px] font-headline text-tertiary">{selectedTile.resource.type?.toUpperCase()}</span>}
              <button onClick={() => setSelectedTile(null)} className="text-zinc-500 hover:text-on-surface"><MS icon="close" className="text-sm" /></button>
            </div>
          </div>

          {/* Content grid */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {selectedTile.irradiatedUntil && selectedTile.irradiatedUntil > Date.now() && (
              <div className="bg-green-900/30 border border-green-500/30 rounded p-2 mb-2 animate-pulse">
                <span className="text-green-400 font-headline text-[10px]">☢️ IRRADIATED</span>
                <span className="text-green-400/70 font-label text-[8px] ml-2">
                  {Math.ceil((selectedTile.irradiatedUntil - Date.now()) / 3600000)}h remaining
                </span>
              </div>
            )}
            {selectedTile.buildings && selectedTile.buildings.length > 0 && (
              <div className="bg-surface-container-low p-2 rounded border border-white/5">
                <h3 className="text-[9px] font-label text-zinc-500 uppercase mb-1 flex items-center gap-1"><MS icon="domain" className="text-[10px]" /> Buildings</h3>
                <div className="space-y-0.5">
                  {selectedTile.buildings.map((b: any, i: number) => {
                    const cfg = BUILDING_CONFIGS[b.type as keyof typeof BUILDING_CONFIGS] || (b.type === "capital" ? { icon: "🏛️", name: "Capital" } as any : null);
                    const isBuilding = b.completedAt && b.completedAt > now;
                    const disabled = b.disabledUntil && b.disabledUntil > now;
                    const siloKey = `${selectedTile.q},${selectedTile.r}`;
                    const buildingMissiles = b.type === "missileSilo" && !isBuilding ? activeTimers.filter(t => t.type === "missile" && t.siloKey === siloKey && t.completesAt > now) : [];
                    const barracksTraining = b.type === "barracks" && !isBuilding ? trainingHexes[`${selectedTile.q},${selectedTile.r}`] : null;
                    const isTraining = barracksTraining && barracksTraining.completesAt > now;
                    void tick;
                  
  // Push notification subscription

  return (
                      <div key={i} className="flex flex-col gap-0.5">
                        <div className="flex items-center justify-between">
                        <span className={`text-[10px] ${disabled ? "text-error" : isBuilding ? "text-primary-container" : "text-on-surface"}`}>
                          {cfg?.icon} {cfg?.name || b.type}
                          {disabled ? ` ⚡${Math.ceil((b.disabledUntil - now) / 1000)}s` : isBuilding ? ` ${Math.ceil((b.completedAt - now) / 1000)}s` : ""}
                        </span>
                        <div className="flex gap-1 items-center">
                        {selectedTile.ownerId === playerId && !isBuilding && b.type !== "capital" && (b.level || 1) < 3 && (
                          <button onClick={(e) => { e.stopPropagation(); const nextLv = (b.level || 1) + 1; const costMult = [1,2,4][nextLv-1]; const costStr = cfg ? Object.entries(cfg.cost).map(([r,a]: any) => `${r}: ${a*costMult}`).join(", ") : ""; setPendingConfirm({ action: "upgrade", q: selectedTile.q, r: selectedTile.r, buildingIndex: i, buildingType: cfg?.name || b.type, level: nextLv, costStr }); }}
                            className="text-[8px] font-headline text-orange-400 border border-orange-400/30 px-1.5 py-0.5 uppercase">⬆ LV{(b.level || 1) + 1}</button>
                        )}
                        {selectedTile.ownerId === playerId && !isBuilding && b.type !== "capital" && (
                          <button onClick={(e) => { e.stopPropagation(); setPendingConfirm({ action: "demolish", name: cfg?.name || b.type, q: selectedTile.q, r: selectedTile.r, buildingIndex: i }); }}
                            className="text-[8px] font-headline text-red-400 border border-red-400/30 px-1.5 py-0.5 uppercase">✕</button>
                        )}
                        {selectedTile.ownerId !== playerId && !isBuilding && !disabled && (
                          <button onClick={() => socketRef.current?.emit("cyber-attack", { targetQ: selectedTile.q, targetR: selectedTile.r, buildingIndex: i })}
                            className="text-[8px] font-headline text-violet-400 border border-violet-400/30 px-1.5 py-0.5 uppercase">HACK</button>
                        )}
                        </div>
                        </div>
                        {cfg?.description && <p className="text-[8px] text-zinc-500 font-label mt-0.5 ml-0.5">{cfg.description}</p>}
                        {cfg?.production && (() => {
                          const bLevel = b.level || 1;
                          const levelMult = [1, 1.5, 2][bLevel - 1] || 1;
                          const tile = selectedTile;
                          let richMult = 1;
                          if (tile?.resource && ["mine", "oilWell", "uraniumExtractor", "rareEarthMine", "compositesPlant"].includes(b.type)) {
                            richMult = tile.resource.richness || 1;
                          }
                          const adjOffsets = [[1,0],[-1,0],[0,1],[0,-1],[1,-1],[-1,1]];
                          const hasPPBoost = b.type !== "powerPlant" && adjOffsets.some(([dq,dr]) => {
                            const at = tiles.find((t: any) => t.q === selectedTile.q + dq && t.r === selectedTile.r + dr);
                            return at?.ownerId === playerId && at?.buildings?.some((bb: any) => bb.type === "powerPlant" && (!bb.completedAt || bb.completedAt <= Date.now()));
                          });
                          const ppMult = hasPPBoost ? 1.5 : 1;
                          const hasEco2 = researchedTechs.includes("eco2");
                          const techMult = hasEco2 ? 1.5 : 1;
                          const totalMult = levelMult * richMult * ppMult * techMult;
                          const boosts: string[] = [];
                          if (bLevel > 1) boosts.push("Lv" + bLevel);
                          if (richMult > 1) boosts.push("x" + richMult + " rich");
                          if (hasPPBoost) boosts.push("PP +50%");
                          if (hasEco2) boosts.push("eco2 +50%");
                          return <p className="text-[8px] text-status-allied/70 font-label ml-0.5">+{Object.entries(cfg.production).map(([r,a]: any) => `${Math.round((a as number) * totalMult * 10) / 10} ${r}`).join(", ")}/tick{boosts.length > 0 ? ` (${boosts.join(", ")})` : ""}</p>;
                        })()}
                        {buildingMissiles.length > 0 && buildingMissiles.map((bm: any, mi: number) => (
                          <div key={mi} className="flex items-center gap-1 ml-4">
                            <span className="text-[7px] font-label text-zinc-500 w-12 truncate">{bm.missileType}</span>
                            <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-error/60" style={{ width: `${Math.max(5, 100 - ((bm.completesAt - now) / ((bm.completesAt - (bm.startedAt || now)) || 1)) * 100)}%` }} /></div>
                            <span className="text-[8px] font-headline text-error">{Math.ceil((bm.completesAt - now) / 1000)}s</span>
                          </div>
                        ))}
                        {isTraining && (() => { void tick; const remaining = Math.ceil((barracksTraining.completesAt - now) / 1000); return (
                          <div className="flex items-center gap-1 ml-4">
                            <span className="text-[7px] font-label text-zinc-500 w-12">⚔ ×{barracksTraining.count}</span>
                            <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-amber-500/60" style={{ width: `${Math.max(5, 100 - (remaining * 1000 / ((barracksTraining.count * (barracksTraining.perTroopMs || 30000)) || 1)) * 100)}%` }} /></div>
                            <span className="text-[8px] font-headline text-amber-400">{remaining}s</span>
                          </div>
                        ); })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {(selectedTile.troops || trainingHexes[`${selectedTile.q},${selectedTile.r}`]) && (
              <div className="bg-surface-container-low p-2 rounded border border-white/5">
                <h3 className="text-[9px] font-label text-zinc-500 uppercase mb-1 flex items-center gap-1"><MS icon="military_tech" className="text-[10px]" /> Forces</h3>
                {selectedTile.troops > 0 && <p className="font-headline text-[10px] text-on-surface">{selectedTile.troops} TROOPS</p>}
                {trainingHexes[`${selectedTile.q},${selectedTile.r}`]?.completesAt > now && (() => { void tick; return (
                  <p className="text-[9px] font-label text-secondary flex items-center gap-1"><MS icon="hourglass_empty" className="text-[10px]" /> Training: {Math.ceil((trainingHexes[`${selectedTile.q},${selectedTile.r}`].completesAt - now) / 1000)}s</p>
                ); })()}
              </div>
            )}
          </div>

          {/* Silo inventory */}
          {selectedTile.ownerId === playerId && siloInventory[`${selectedTile.q},${selectedTile.r}`]?.length > 0 && (
            <div className="mb-3 flex gap-1 flex-wrap">
              {siloInventory[`${selectedTile.q},${selectedTile.r}`].map((type: string, i: number) => (
                <button key={i} onClick={() => { setTargetingMode(`missile:${type}:${selectedTile.q},${selectedTile.r}`); setSelectedTile(null); }}
                  className="text-[9px] font-headline text-error border border-error/50 bg-error/10 px-2 py-1 uppercase active:scale-95 transition-all">LAUNCH {type}</button>
              ))}
            </div>
          )}

          {/* Battery ammo + craft */}
          {selectedTile.ownerId === playerId && selectedTile.buildings?.some((b: any) => b.type === "antiMissileBattery") && (() => {
            const bKey = `${selectedTile.q},${selectedTile.r}`;
            const ammo = batteryAmmo[bKey] || { count: 0, max: 5 };
            return (
              <div className="mb-3 bg-blue-900/20 border border-blue-500/30 rounded p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-headline text-blue-400 uppercase tracking-widest">🛡️ COUNTER-MISSILES</span>
                  <span className="text-[10px] font-bold text-blue-300">{ammo.count}/{ammo.max}</span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${(ammo.count / ammo.max) * 100}%` }} />
                </div>
                {(() => {
                  const building = batteryBuilding[bKey];
                  if (building && building > Date.now()) {
                    const remaining = Math.ceil((building - Date.now()) / 1000);
                    const mins = Math.floor(remaining / 60);
                    const secs = remaining % 60;
                    return <div className="text-[9px] text-yellow-400 font-headline uppercase text-center py-1.5 animate-pulse">⏳ BUILDING... {mins}:{secs.toString().padStart(2, "0")}</div>;
                  }
                  if (ammo.count < ammo.max) return (
                    <button onClick={() => { socketRef.current?.emit("craft-counter-missile", { q: selectedTile.q, r: selectedTile.r }); }}
                      className="w-full text-[9px] font-headline text-blue-300 border border-blue-500/50 bg-blue-500/10 px-2 py-1.5 uppercase active:scale-95 transition-all">
                      CRAFT ($200 + 30🧱 + 20📟 + 15💣) ~5min
                    </button>
                  );
                  return null;
                })()}
                {ammo.count === 0 && <p className="text-[8px] text-red-400 mt-1">⚠️ NO AMMO — battery cannot intercept!</p>}
              </div>
            );
          })()}

          {/* Commander on this hex */}
          {(() => {
            const hexCmd = commanders.find((c: any) => c.assignedHex && c.assignedHex.q === selectedTile.q && c.assignedHex.r === selectedTile.r && c.status === "active");
            if (!hexCmd) return null;
            const specIcons: Record<string, string> = { warfare: "⚔️", logistics: "📦", intelligence: "🕵️", fortification: "🛡️", diplomacy: "🤝" };
            return (
              <div className="mb-3 p-2 bg-orange-900/10 border border-orange-500/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-base">{specIcons[hexCmd.specialty] || "🎖️"}</span>
                    <div>
                      <span className="font-headline text-[11px] text-orange-400">{hexCmd.name}</span>
                      <span className="text-[9px] text-zinc-500 ml-1.5">Lv.{hexCmd.level} {hexCmd.specialty}</span>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { haptic("medium"); setTargetingMode("commander:" + hexCmd.id); setSelectedTile(null); }}
                      className="text-[8px] text-orange-400 border border-orange-500/30 px-2 py-0.5 font-headline active:scale-95">MOVE</button>
                    <button onClick={() => { socketRef.current?.emit("unassign-commander", { commanderId: hexCmd.id }); }}
                      className="text-[8px] text-red-400 border border-red-500/30 px-2 py-0.5 font-headline active:scale-95">RECALL</button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Action buttons */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {selectedTile.ownerId === playerId && !(selectedTile.buildings?.length > 0) && (
              <button onClick={() => setShowBuildMenu(true)} className="flex-shrink-0 bg-surface-container-highest border border-white/10 px-4 py-2 font-headline text-[10px] uppercase text-on-surface active:scale-95 transition-all">BUILD</button>
            )}
            {selectedTile.ownerId === playerId && (<>
              {selectedTile.buildings?.some((b: any) => b.type === "barracks" && (!b.completedAt || b.completedAt <= now)) && (<>
                <button onClick={() => { haptic("light"); optimisticTrain(selectedTile.q, selectedTile.r, 1); socketRef.current?.emit("train-troops", { q: selectedTile.q, r: selectedTile.r, count: 1 }); }}
                  className="flex-shrink-0 bg-surface-container-highest border border-white/10 px-4 py-2 font-headline text-[10px] uppercase text-on-surface active:scale-95 transition-all">TRAIN 1</button>
                <button onClick={() => { optimisticTrain(selectedTile.q, selectedTile.r, 5); socketRef.current?.emit("train-troops", { q: selectedTile.q, r: selectedTile.r, count: 5 }); }}
                  className="flex-shrink-0 bg-surface-container-highest border border-white/10 px-4 py-2 font-headline text-[10px] uppercase text-on-surface active:scale-95 transition-all">TRAIN 5</button>
              </>)}
              {(selectedTile.troops || 0) > 0 && (<>
                <button onClick={() => { setMoveFrom({ q: selectedTile.q, r: selectedTile.r, count: Math.ceil((selectedTile.troops || 0) / 2) }); setTargetingMode("move"); setSelectedTile(null); }}
                  className="flex-shrink-0 bg-surface-container-highest border border-white/10 px-4 py-2 font-headline text-[10px] uppercase text-on-surface active:scale-95 transition-all">MOVE ½</button>
                <button onClick={() => { setMoveFrom({ q: selectedTile.q, r: selectedTile.r, count: selectedTile.troops || 0 }); setTargetingMode("move"); setSelectedTile(null); }}
                  className="flex-shrink-0 bg-surface-container-highest border border-white/10 px-4 py-2 font-headline text-[10px] uppercase text-on-surface active:scale-95 transition-all">MOVE ALL</button>
              </>)}
              {selectedTile.buildings?.some((b: any) => b.type === "missileSilo" && (!b.completedAt || b.completedAt <= now)) && (
                <button onClick={() => setShowMissileMenu({q: selectedTile.q, r: selectedTile.r})}
                  className="flex-shrink-0 border border-error/50 bg-error/10 px-4 py-2 font-headline text-[10px] uppercase text-error active:scale-95 transition-all">BUILD MISSILE</button>
              )}
            </>)}
            {selectedTile.ownerId !== playerId && selectedTile.biome !== "ocean" && (() => {
              const adjOwned = tiles.filter(t => t.ownerId === playerId && (t.troops || 0) > 0).find(t => {
                const d = Math.max(Math.abs(t.q - selectedTile.q), Math.abs(t.r - selectedTile.r), Math.abs((t.q+t.r) - (selectedTile.q+selectedTile.r)));
                return d === 1;
              });
              if (!adjOwned) return null;
            
  // Push notification subscription

  return (<>
                {selectedTile.ownerId && (
                  <button onClick={() => { setPendingConfirm({ action: "attack", fromQ: adjOwned.q, fromR: adjOwned.r, toQ: selectedTile.q, toR: selectedTile.r, troops: adjOwned.troops }); }}
                    className="flex-shrink-0 border border-error/50 bg-error/20 px-4 py-2 font-headline text-[10px] uppercase text-error active:scale-95 transition-all">⚔ ATTACK ({adjOwned.troops})</button>
                )}
                {!selectedTile.ownerId && (
                  <button onClick={() => { optimisticMove(adjOwned.q, adjOwned.r, selectedTile.q, selectedTile.r, adjOwned.troops); socketRef.current?.emit("move-troops", { fromQ: adjOwned.q, fromR: adjOwned.r, toQ: selectedTile.q, toR: selectedTile.r, count: adjOwned.troops }); setSelectedTile(null); }}
                    className="flex-shrink-0 border border-amber-500/50 bg-amber-500/10 px-4 py-2 font-headline text-[10px] uppercase text-amber-400 active:scale-95 transition-all">MOVE IN ({adjOwned.troops})</button>
                )}
                {!selectedTile.ownerId && (selectedTile.troops || 0) > 0 && (
                  <button onClick={() => { optimisticClaim(selectedTile.q, selectedTile.r); socketRef.current?.emit("claim-hex", { q: selectedTile.q, r: selectedTile.r }); }}
                    className="flex-shrink-0 border border-status-allied/50 bg-status-allied/10 px-4 py-2 font-headline text-[10px] uppercase text-status-allied active:scale-95 transition-all">CLAIM HEX</button>
                )}
              </>);
            })()}
            {selectedTile.ownerId !== playerId && (selectedTile.troops || 0) > 0 && (<>
              <button onClick={() => { setMoveFrom({ q: selectedTile.q, r: selectedTile.r, count: Math.ceil((selectedTile.troops || 0) / 2) }); setTargetingMode("move"); setSelectedTile(null); }}
                className="flex-shrink-0 bg-surface-container-highest border border-white/10 px-4 py-2 font-headline text-[10px] uppercase text-on-surface active:scale-95 transition-all">MOVE ½</button>
              <button onClick={() => { setMoveFrom({ q: selectedTile.q, r: selectedTile.r, count: selectedTile.troops || 0 }); setTargetingMode("move"); setSelectedTile(null); }}
                className="flex-shrink-0 bg-surface-container-highest border border-white/10 px-4 py-2 font-headline text-[10px] uppercase text-on-surface active:scale-95 transition-all">MOVE ALL</button>
            </>)}
            {!selectedTile.ownerId && selectedTile.biome !== "ocean" && (
              <button onClick={() => { haptic("medium"); optimisticClaim(selectedTile.q, selectedTile.r); socketRef.current?.emit(SOCKET_EVENTS.CLAIM_HEX, { q: selectedTile.q, r: selectedTile.r }); }}
                className="flex-1 border border-primary-container text-primary-container font-headline text-[10px] py-2 uppercase active:scale-95 transition-all">CLAIM (${(() => { const owned = Object.values(tiles).filter((t:any) => t.ownerId === player?.id).length; return 50 + owned * 20; })()})</button>
            )}
          </div>
        </div>
      )}

      {/* Build menu */}
      {showBuildMenu && selectedTile && (<>
        <div className="fixed inset-0" style={{ zIndex: 39 }} onClick={() => setShowBuildMenu(false)} />
        <div className="ip-slide-up fixed bottom-14 left-0 w-full z-40 glass-panel border-t border-orange-500/30 px-4 pt-3 pb-4 max-h-[350px] overflow-y-auto">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-headline text-sm text-primary-container tracking-widest">BUILD</h2>
            <button onClick={() => setShowBuildMenu(false)}><MS icon="close" className="text-zinc-500 text-sm" /></button>
          </div>
          {Object.values(BUILDING_CONFIGS).map(cfg => {
            const canBuild = (!cfg.requiresBiome || cfg.requiresBiome.includes(selectedTile.biome)) && (!cfg.requiresResource || selectedTile.resource?.type === cfg.requiresResource) && (!cfg.requiresTech || researchedTechs.includes(cfg.requiresTech));
            if (selectedTile.buildings?.some((b: any) => b.type === cfg.type)) return null;
          
  // Push notification subscription

  return (
              <div key={cfg.type} onClick={() => { if (canBuild) { haptic("medium"); optimisticBuild(selectedTile.q, selectedTile.r, cfg.type); socketRef.current?.emit(SOCKET_EVENTS.BUILD, { q: selectedTile.q, r: selectedTile.r, buildingType: cfg.type }); setShowBuildMenu(false); } }}
                className={`p-3 mb-1.5 rounded border border-white/5 ${canBuild ? "bg-surface-container-low hover:bg-surface-container cursor-pointer" : "bg-surface-container-low/30 opacity-40"} transition-all`}>
                <div className="flex justify-between items-center">
                  <span className="font-headline text-[11px] text-on-surface uppercase">{cfg.icon} {cfg.name}</span>
                  {cfg.description && <p className="text-[8px] text-zinc-500 font-label mt-0.5">{cfg.description}</p>}
                  {cfg.type === "missileSilo" && <div className="text-[7px] text-orange-400/70 font-mono mt-0.5">
                    {Object.entries(MISSILE_CONFIG).map(([t, m]: [string, any]) => <span key={t} className="mr-2">{t.toUpperCase()}: R{m.range === -1 ? "∞" : m.range} D{m.damage}</span>)}
                  </div>}
                  {cfg.type === "antiMissileBattery" && <p className="text-[7px] text-blue-400/70 font-mono mt-0.5">Range: 4 hexes · Craft: $200+30🧱+20📟+15💣 · Max 5</p>}
                  {cfg.production && <p className="text-[8px] text-status-allied/70 font-label">+{Object.entries(cfg.production).map(([r,a]: any) => `${a} ${r}`).join(", ")}/tick</p>}
                  {cfg.requiresTech && !researchedTechs.includes(cfg.requiresTech) && <span className="text-error text-[9px] font-headline flex items-center gap-0.5"><MS icon="lock" className="text-[10px]" />{cfg.requiresTech}</span>}
                </div>
                <p className="text-[9px] font-label mt-0.5">{Object.entries(cfg.cost).map(([r, a]) => {
                  const have = Math.floor((player?.resources as any)?.[r] || 0);
                  const enough = have >= (a as number);
                  return <span key={r} className={enough ? "text-zinc-500" : "text-error font-bold"}>{a as number} {r} </span>;
                })} <span className="text-zinc-600">— {cfg.buildTime}s</span></p>
              </div>
            );
          })}
        </div></>
      )}

      {/* === CHAT PANEL === */}
      {(activeTab === "chat" || closingTab === "chat") && (
        <div className={`${closingTab === "chat" ? "ip-screen-out" : "ip-slide-up"} ip-panels-top fixed bottom-14 left-0 right-0 glass-panel z-40 flex flex-col`}>
          <div className="flex gap-0 border-b border-white/5 overflow-x-auto no-scrollbar">
            <button onClick={() => setChatTarget(null)} className={`px-4 py-2 font-headline text-[10px] tracking-widest border-b-2 transition-all ${chatTarget === null ? "border-primary-container text-primary-container bg-primary-container/5" : "border-transparent text-zinc-500"}`}>GLOBAL</button>
            {otherPlayers.map(p => (
              <button key={p.id} onClick={() => setChatTarget(p.id)} className={`px-3 py-2 font-headline text-[10px] tracking-tighter border-b-2 transition-all whitespace-nowrap ${chatTarget === p.id ? "border-primary-container text-primary-container" : "border-transparent text-zinc-500"}`}>{p.name}</button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
            {chatMessages.filter((m: any) => chatTarget === null ? !m.to : (m.from === chatTarget && m.to === playerId) || (m.from === playerId && m.to === chatTarget)).map((msg: any, i: number) => (
              <div key={i} className="text-sm"><span className="font-headline text-[11px]" style={{ color: playerColors[msg.from] || "#888" }}>{msg.fromName}: </span><span className="text-on-surface/80">{msg.message}</span></div>
            ))}
          </div>
          <div className="flex gap-2 p-2 border-t border-white/5">
            <input value={chatInput} onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && chatInput.trim()) { haptic("light"); socketRef.current?.emit("chat-send", { message: chatInput.trim(), to: chatTarget || undefined }); setChatInput(""); } }}
              placeholder={chatTarget ? "DM..." : "Global..."} className="flex-1 bg-surface-container border border-outline-variant/20 rounded px-3 py-2 text-on-surface font-body text-sm outline-none" />
            <button onClick={() => { if (chatInput.trim()) { haptic("light"); socketRef.current?.emit("chat-send", { message: chatInput.trim(), to: chatTarget || undefined }); setChatInput(""); } }}
              className="px-4 py-2 bg-primary-container text-background font-headline text-[10px] tracking-wider uppercase active:scale-95 transition-all">SEND</button>
          </div>
        </div>
      )}

      {/* === TECH PANEL === */}
      {(activeTab === "tech" || closingTab === "tech") && (
        <div className={`${closingTab === "tech" ? "ip-screen-out" : "ip-slide-up"} ip-panels-top fixed bottom-14 left-0 right-0 bg-background z-40 overflow-y-auto p-4`}>
          <div className="border-l-2 border-primary-container pl-3 mb-6">
            <h1 className="font-headline text-xl font-black uppercase tracking-tighter text-on-surface">TECH_TREE</h1>
          </div>
          {["military", "defense", "economy", "intelligence"].map(branch => {
            const branchColors: Record<string, string> = { military: "text-primary-container border-primary-container", defense: "text-secondary border-secondary", economy: "text-tertiary border-tertiary", intelligence: "text-outline border-outline" };
            const icons: Record<string, string> = { military: "military_tech", defense: "shield", economy: "monetization_on", intelligence: "radar" };
          
  // Push notification subscription

  return (
              <div key={branch} className="mb-6">
                <div className={`flex items-center justify-between border-b ${branchColors[branch].split(" ")[1]}/20 pb-1 mb-2`}>
                  <h2 className={`font-headline text-sm ${branchColors[branch].split(" ")[0]} uppercase`}>{branch}</h2>
                  <MS icon={icons[branch]} className={`text-sm ${branchColors[branch].split(" ")[0]}`} />
                </div>
                {Object.values(TECH_TREE).filter((t: any) => t.branch === branch).sort((a: any, b: any) => a.tier - b.tier).map((tech: any) => {
                  const researched = researchedTechs.includes(tech.id);
                  const researching = researchingTech === tech.id;
                  const canResearch = !researched && !researchingTech && (!tech.requires || tech.requires.every((r: string) => researchedTechs.includes(r)));
                  void tick;
                
  // Push notification subscription

  return (
                    <div key={tech.id}
                      className={`bg-surface-container-low p-3 rounded mb-1.5 border transition-all ${researched ? "border-status-allied/30" : researching ? "border-primary-container/40 ring-1 ring-primary-container/20" : canResearch ? "border-white/5" : "border-white/5 opacity-50"}`}>
                      <div className="flex justify-between items-center">
                        <h3 className="font-headline text-[11px] font-bold uppercase">{tech.name}</h3>
                        <span className={`text-[9px] font-headline ${researched ? "text-status-allied" : researching ? "text-primary-container animate-pulse" : "text-zinc-500"}`}>
                          {researched ? "ARCHIVED" : researching && researchEndsAt > now ? `RESEARCHING ${Math.ceil((researchEndsAt - now) / 1000)}s` : !canResearch ? "" : "AVAILABLE"}
                          {!canResearch && !researched && !researching && <MS icon="lock" className="text-[10px] ml-1" />}
                        {canResearch && <button onClick={() => { haptic("medium"); sfxResearch(); socketRef.current?.emit("research-start", { techId: tech.id }); }} className="ml-2 bg-primary-container text-on-primary-container px-3 py-0.5 text-[9px] font-headline uppercase active:scale-95 transition-all rounded">RESEARCH</button>}
                        </span>
                      </div>
                      <p className="text-[10px] text-zinc-400 mt-0.5">{tech.description}</p>
                      {researching && researchEndsAt > now && <div className="w-full h-1 bg-zinc-800 mt-2 rounded-full overflow-hidden"><div className="h-full bg-primary-container" style={{ width: `${Math.max(5, 100 - ((researchEndsAt - now) / (tech.researchTime * 1000)) * 100)}%` }} /></div>}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* World Projects */}
          <div className="mt-8 mb-20">
            <div className="flex items-center gap-3 mb-4"><div className="w-6 h-[2px] bg-primary-container" /><h2 className="font-headline text-lg font-bold tracking-widest uppercase">WORLD_PROJECTS</h2></div>
            <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
              {[
                { id: "commandNexus", name: "Command Nexus", icon: "hub", effect: "+15% spy success" },
                { id: "industrialMegaplex", name: "Industrial Megaplex", icon: "factory", effect: "+40% production" },
                { id: "ironCitadel", name: "Iron Citadel", icon: "fort", effect: "85% intercept" },
                { id: "broadcastTower", name: "Broadcast Tower", icon: "cell_tower", effect: "See all diplomacy" },
                { id: "strategicVault", name: "Strategic Vault", icon: "account_balance", effect: "2x storage" },
                { id: "deepStrikeSilo", name: "Deep Strike Silo", icon: "rocket_launch", effect: "3x ICBM speed" },
                { id: "uraniumComplex", name: "Uranium Complex", icon: "science", effect: "½ nuke build" },
              ].map(p => {
                const wp = worldProjects.find((w: any) => w.projectId === p.id);
              
  // Push notification subscription

  return (
                  <div key={p.id} className={`min-w-[200px] flex-shrink-0 bg-surface-container-highest/30 backdrop-blur-md p-4 rounded-xl border-t ${wp?.completed ? "border-primary-container/50" : "border-zinc-700/30"}`}>
                    <div className="flex justify-between items-start mb-3">
                      <MS icon={p.icon} className={`text-3xl ${wp?.completed ? "text-primary-container" : "text-zinc-500"}`} />
                      <span className={`font-headline text-[9px] px-1.5 py-0.5 ${wp?.completed ? "text-primary-container bg-primary-container/10" : "text-zinc-500 bg-zinc-800"}`}>{wp?.completed ? "BUILT" : "IDLE"}</span>
                    </div>
                    <h3 className="font-headline text-[11px] font-bold mb-0.5">{p.name}</h3>
                    <p className="text-[10px] text-zinc-400 font-body mb-2">{p.effect}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* === DIPLOMACY PANEL === */}
      {(activeTab === "diplomacy" || closingTab === "diplomacy") && (
        <div className={`${closingTab === "diplomacy" ? "ip-screen-out" : "ip-slide-up"} ip-panels-top fixed bottom-14 left-0 right-0 bg-background z-40 overflow-y-auto p-4`}>
          {/* Sub-tabs */}
          <div className="flex gap-0 mb-4 border-b border-white/5">
            <button onClick={() => setDiploView("players")} className={`px-6 py-2 font-headline text-[10px] tracking-widest border-b-2 transition-all ${diploView === "players" ? "border-primary-container text-primary-container bg-primary-container/5" : "border-transparent text-zinc-500"}`}>PLAYERS</button>
            <button onClick={() => setDiploView("government")} className={`px-6 py-2 font-headline text-[10px] tracking-widest border-b-2 transition-all ${diploView === "government" ? "border-primary-container text-primary-container bg-primary-container/5" : "border-transparent text-zinc-500"}`}>GOVERNMENT</button>
            <button onClick={() => { setDiploView("leaderboard" as any); socketRef.current?.emit("get-leaderboard"); }} className={`px-6 py-2 font-headline text-[10px] tracking-widest border-b-2 transition-all ${diploView === "leaderboard" ? "border-primary-container text-primary-container bg-primary-container/5" : "border-transparent text-zinc-500"}`}>RANKINGS</button>
          </div>

          {diploView === "players" && (<>
            {pendingProposals.filter(p => p.to === playerId).map((p, i) => (
              <div key={i} className="bg-primary-container/5 border border-primary-container/20 rounded p-3 mb-3">
                <p className="text-[11px] text-primary-container font-headline">{otherPlayers.find(op => op.id === p.from)?.name} proposes {p.type}</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => { sfxDiplomacy(); socketRef.current?.emit("diplomacy-respond", { proposalId: p.id, accept: true }); }} className="px-3 py-1 bg-status-allied text-background font-headline text-[10px]">Accept</button>
                  <button onClick={() => socketRef.current?.emit("diplomacy-respond", { proposalId: p.id, accept: false })} className="px-3 py-1 bg-error text-background font-headline text-[10px]">Decline</button>
                </div>
              </div>
            ))}
            <div className="space-y-2">
              {otherPlayers.map(p => {
                const rel = playerRelations[p.id] || { status: "neutral" };
                const borderColors: Record<string, string> = { neutral: "border-status-neutral", allied: "border-status-allied", war: "border-status-war", nap: "border-secondary" };
                const statusBg: Record<string, string> = { neutral: "bg-status-neutral/10 text-status-neutral border-status-neutral/30", allied: "bg-status-allied/10 text-status-allied border-status-allied/30", war: "bg-status-war/10 text-status-war border-status-war/30", nap: "bg-secondary/10 text-secondary border-secondary/30" };
              
  // Push notification subscription

  return (
                  <div key={p.id} className={`bg-surface-container-low/80 border-l-2 ${borderColors[rel.status] || "border-zinc-600"} border border-outline-variant/10 p-3 rounded-lg`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded" style={{ background: p.color }} />
                        <h3 className="font-headline text-sm font-bold text-on-surface">{p.name}</h3>
                        <span className={`text-[9px] font-headline px-1.5 border rounded ${statusBg[rel.status] || "text-zinc-500"}`}>{rel.status.toUpperCase()}</span>
                        {p.dormancyStatus && p.dormancyStatus !== "active" && <span className="text-[9px]">{p.dormancyStatus === "dormant" ? "💤" : p.dormancyStatus === "deep_dormant" ? "🛡️" : "🏚️"}</span>}
                      </div>
                      <span className={`text-[9px] font-label ${p.online ? "text-status-allied" : "text-zinc-500"}`}>{p.online ? "ONLINE" : p.lastSeen ? `OFF ${(() => { const m = Math.floor((Date.now() - p.lastSeen) / 60000); return m >= 1440 ? Math.floor(m/1440) + 'd ago' : m >= 60 ? Math.floor(m/60) + 'h ago' : m + 'm ago'; })()}` : "OFFLINE"}</span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      <button onClick={() => { setChatTarget(p.id); switchTab("chat"); }} className="p-1.5 bg-white/5 border border-white/10 rounded-lg"><MS icon="forum" className="text-sm text-zinc-400" /></button>
                      {rel.status === "neutral" && (<>
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "proposeAlliance" })} className="px-3 py-1.5 border border-status-allied text-status-allied font-headline text-[9px]">ALLIANCE</button>
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "proposeNap" })} className="px-3 py-1.5 border border-secondary text-secondary font-headline text-[9px]">NAP</button>
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "denounce" })} className="px-3 py-1.5 border border-primary-container/50 text-primary-container font-headline text-[9px]">DENOUNCE</button>
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "declareWar" })} className="px-3 py-1.5 bg-status-war/20 border border-status-war text-status-war font-headline text-[9px]">WAR</button>
                      </>)}
                      {rel.status === "allied" && (<>
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "denounce" })} className="px-3 py-1.5 border border-primary-container/50 text-primary-container font-headline text-[9px]">BREAK</button>
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "declareWar" })} className="px-3 py-1.5 bg-status-war/20 border border-status-war text-status-war font-headline text-[9px]">WAR</button>
                      </>)}
                      {rel.status === "war" && (
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "proposeCeasefire" })} className="px-3 py-1.5 border border-status-allied text-status-allied font-headline text-[9px]">CEASEFIRE</button>
                      )}
                      {rel.status === "nap" && (<>
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "proposeAlliance" })} className="px-3 py-1.5 border border-status-allied text-status-allied font-headline text-[9px]">UPGRADE</button>
                        <button onClick={() => socketRef.current?.emit(SOCKET_EVENTS.DIPLOMACY, { targetId: p.id, action: "denounce" })} className="px-3 py-1.5 border border-primary-container/50 text-primary-container font-headline text-[9px]">BREAK</button>
                      </>)}
                    </div>
                  </div>
                );
              })}
            </div>
          </>)}

          {diploView === "government" && (
            <div className="space-y-2 pb-20">
              {/* Political Will bar */}
              <div className="bg-surface-container-low p-3 border border-outline-variant/20 rounded-lg mb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-headline text-[10px] text-primary-container">Political Will</span>
                  <span className="font-headline text-sm text-primary-container">{Math.floor(politicalWill)}/20</span>
                </div>
                <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden"><div className="bg-primary-container h-full shadow-[0_0_10px_#ff8c00]" style={{ width: `${(politicalWill / 20) * 100}%` }} /></div>
                {stanceChangedAt > 0 && (now - stanceChangedAt) < 4 * 3600000 && (
                  <p className="text-[9px] font-headline text-zinc-500 mt-1">Cooldown: {Math.ceil((4 * 3600000 - (now - stanceChangedAt)) / 60000)}min</p>
                )}
              </div>
              {[
                { id: "military", icon: "military_tech", name: "MILITARY_FOCUS", bonus: "Unit Damage +25%", penalty: "Production -20%" },
                { id: "trade", icon: "payments", name: "TRADE_DOMINANCE", bonus: "Port Revenue +30%", penalty: "Military -15%" },
                { id: "surveillance", icon: "visibility", name: "DEEP_SURVEILLANCE", bonus: "Spy Success +40%", penalty: "Stability -15%" },
                { id: "fortified", icon: "fort", name: "FORTIFIED_BORDER", bonus: "Intercept +25%", penalty: "Expansion -20%" },
                { id: "expansionist", icon: "explore", name: "EXPANSIONIST_DRIVE", bonus: "Claim Cost -30%", penalty: "Intercept -20%" },
              ].map(s => {
                const isActive = myStance === s.id;
                const canChange = politicalWill >= 10 && (!stanceChangedAt || (now - stanceChangedAt) >= 4 * 3600000) && !isActive;
              
  // Push notification subscription

  return (
                  <div key={s.id}
                    className={`p-3 rounded-lg border transition-all ${isActive ? "border-primary-container/50 bg-primary-container/5" : "border-white/5"} relative`}>
                    {isActive && <div className="absolute top-0 right-0 h-full w-1 bg-primary-container" />}
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex items-center gap-2">
                        <MS icon={s.icon} className={`${isActive ? "text-primary-container" : "text-zinc-500"}`} filled={isActive} />
                        <h4 className={`font-headline text-[11px] font-bold ${isActive ? "text-primary-container" : "text-on-surface"}`}>{s.name}</h4>
                      </div>
                      {isActive && <span className="text-[8px] font-headline text-primary-container uppercase px-2 py-0.5 border border-primary-container/30 bg-primary-container/10">ACTIVE</span>}
                      {canChange && <button onClick={() => socketRef.current?.emit("change-stance", { stance: s.id })} className="text-[8px] font-headline text-primary-container uppercase px-3 py-1 border border-primary-container/40 bg-primary-container/10 active:scale-95 transition-all rounded">ADOPT ({politicalWill}/10 PW)</button>}
                    </div>
                    <div className="flex flex-col gap-0.5 pl-8">
                      <div className="flex justify-between"><span className="text-[10px] font-label text-zinc-300">{s.bonus.split(" ").slice(0, -1).join(" ")}</span><span className="text-[10px] font-headline text-status-allied font-bold">{s.bonus.split(" ").pop()}</span></div>
                      <div className="flex justify-between"><span className="text-[10px] font-label text-zinc-300">{s.penalty.split(" ").slice(0, -1).join(" ")}</span><span className="text-[10px] font-headline text-status-war font-bold">{s.penalty.split(" ").pop()}</span></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {diploView === "leaderboard" && (
            <div className="space-y-2 pb-20">
              {leaderboard.length === 0 && <p className="text-zinc-500 text-xs font-label text-center py-8">Loading rankings...</p>}
              {leaderboard.map((p: any, i: number) => (
                <div key={p.id} className={`flex items-center gap-3 p-3 rounded-lg border ${i === 0 ? "border-primary-container/30 bg-primary-container/5" : "border-white/5 bg-surface-container-low/50"}`}>
                  <span className={`font-headline text-lg w-8 text-center ${i === 0 ? "text-primary-container" : i === 1 ? "text-zinc-300" : i === 2 ? "text-amber-700" : "text-zinc-500"}`}>{i + 1}</span>
                  <span className="w-3 h-3 rounded-sm" style={{ background: p.color }}></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-headline text-sm text-on-surface truncate">{p.name}</span>
                      <span className={`w-1.5 h-1.5 rounded-full ${p.online ? "bg-green-400 animate-pulse" : "bg-zinc-600"}`}></span>
                    </div>
                    <div className="flex gap-3 mt-0.5">
                      <span className="text-[9px] font-label text-zinc-400"><MS icon="hexagon" className="text-[9px]" />{p.territory}</span>
                      <span className="text-[9px] font-label text-zinc-400"><MS icon="military_tech" className="text-[9px]" />{p.troops}</span>
                      <span className="text-[9px] font-label text-zinc-400"><MS icon="domain" className="text-[9px]" />{p.buildings}</span>
                      <span className="text-[9px] font-label text-zinc-400"><MS icon="biotech" className="text-[9px]" />{p.techCount}</span>
                      <span className="text-[9px] font-label text-green-400/60">+{p.totalIncome}/t</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-headline text-sm text-primary-container">{p.overallScore}</span>
                    <p className="text-[8px] font-label text-zinc-500">SCORE</p>
                  </div>
                </div>
              ))}
            </div>
          )}        </div>
      )}

      {/* === INTEL PANEL === */}
      {(activeTab === "events" || closingTab === "events") && (
        <div className={`${closingTab === "events" ? "ip-screen-out" : "ip-slide-up"} ip-panels-top fixed bottom-14 left-0 right-0 bg-background z-40 overflow-hidden flex flex-col`}>
          


            <div className="mb-3 p-3 bg-surface-container rounded-lg border border-white/5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-headline text-[11px] text-on-surface-variant uppercase tracking-wider">🔔 Push Notifications</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${notifStatus === "subscribed" ? "bg-green-900/50 text-green-400" : notifStatus === "unavailable" ? "bg-red-900/50 text-red-400" : "bg-yellow-900/50 text-yellow-400"}`}>{notifStatus === "subscribed" ? "ACTIVE" : notifStatus === "checking" ? "CHECKING..." : notifStatus === "unavailable" ? "NOT AVAILABLE" : "NOT SET UP"}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={async () => {
                try {
                  if (!("serviceWorker" in navigator)) { showToast("No Service Worker", "danger"); return; }
                  if (!("PushManager" in window)) { showToast("No PushManager - Add to Home Screen first", "danger"); return; }
                  const perm = await Notification.requestPermission();
                  if (perm !== "granted") { showToast("Permission: " + perm, "danger"); return; }
                  showToast("Permission granted!");
                  showToast("Getting service worker...");
                  const reg = await navigator.serviceWorker.ready;
                  showToast("Got SW. Fetching VAPID key...");
                  const vr = await fetch("/api/push/vapid-key");
                  const { publicKey } = await vr.json();
                  showToast("VAPID OK. Creating subscription...");
                  let sub = await reg.pushManager.getSubscription();
                  if (!sub) {
                    const k = Uint8Array.from(atob(publicKey.replace(/-/g,"+").replace(/_/g,"/")), c => c.charCodeAt(0));
                    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: k });
                  }
                  showToast("Subscription created! Saving...");
                  const saveRes = await fetch("/api/push/subscribe", { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify({ playerId, subscription: sub.toJSON() }) });
                  const saveData = await saveRes.json();
                  showToast("Saved: " + JSON.stringify(saveData), "success"); setNotifStatus("subscribed");
                } catch(e: any) { showToast("Failed: " + (e.message||e), "danger"); setNotifStatus("failed"); }
              }} className="flex-1 border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 rounded font-headline text-[10px] uppercase text-amber-400 active:scale-95">{notifStatus === "subscribed" ? "RE-SUBSCRIBE" : "ENABLE"}</button>
              <button onClick={async () => {
                try {
                  const r = await fetch("/api/push/test", { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify({playerId}) });
                  const d = await r.json();
                  showToast(d.ok ? `Test sent to ${d.sent} device(s)! Check your notifications.` : `No subscription found — tap ENABLE first`, d.ok ? "success" : "error");
                } catch(e) { showToast("Test failed", "error"); }
              }} className="flex-1 border border-violet-500/50 bg-violet-500/10 px-3 py-1.5 rounded font-headline text-[10px] uppercase text-violet-400 active:scale-95">🧪 TEST</button>
            </div>
            <p className="text-[9px] text-on-surface-variant/50 mt-1.5">iOS: Add to Home Screen from Safari first • Android: works in any browser</p>
          </div>
          {/* Live Feed */}
          <div className="flex items-center gap-2 mb-3 border-b border-white/5 pb-2">
            <div className="w-1 h-4 bg-primary-container" />
            <h2 className="font-headline text-sm tracking-widest text-primary-container">LIVE_INTEL_FEED</h2>
          </div>
          <div className="space-y-1.5 mb-8 max-h-[250px] overflow-y-auto pr-1">
            {eventFeed.slice().reverse().slice(0, 30).map((ev: any, i: number) => {
              const borderColor = ev.type === "nuclear" || ev.type === "war" || ev.type === "missile" ? "border-error/50" : ev.type === "milestone" || ev.type === "wonder" ? "border-primary-container/50" : ev.type === "diplomacy" ? "border-tertiary/50" : "border-zinc-600/50";
            
  // Push notification subscription

  return (
                <div key={i} className={`flex gap-3 p-2 border-l-4 ${borderColor} bg-surface-container-low/30 items-center`}>
                  <span className="font-headline text-[9px] text-zinc-500 shrink-0">{ev.time ? new Date(ev.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--:--"}</span>
                  <p className="text-[11px] font-body text-on-surface uppercase tracking-wide flex-1">{ev.message}</p>
                </div>
              );
            })}
          </div>

          {/* Espionage */}
          <div className="flex items-center justify-between mb-3 border-b border-violet-500/20 pb-2">
            <div className="flex items-center gap-2"><div className="w-1 h-4 bg-violet-500" /><h2 className="font-headline text-sm tracking-widest text-violet-400">ESPIONAGE</h2></div>
            {(() => {
              const hasInt2 = player?.techResearched?.includes("int2");
              const hasAcademy = tiles.some(t => t.ownerId === playerId && t.buildings?.some((b: any) => b.type === "spyAcademy" && (!b.completedAt || b.completedAt <= Date.now())));
              const canTrain = hasInt2 && hasAcademy;
              return (
                <button onClick={() => { if (canTrain) { haptic("medium"); socketRef.current?.emit("train-spy"); } }}
                  className={`font-headline text-[9px] px-3 py-1 border uppercase ${canTrain ? "bg-violet-900/20 text-violet-400 border-violet-500/30 active:scale-95" : "bg-zinc-900/20 text-zinc-600 border-zinc-700/30 cursor-not-allowed"}`}>
                  {!hasInt2 ? "NEED INT-2 TECH" : !hasAcademy ? "NEED SPY ACADEMY" : "TRAIN SPY"}
                </button>
              );
            })()}
          </div>
          {mySpies.length === 0 && <p className="text-[10px] text-zinc-500 mb-4">No agents deployed.</p>}
          <div className="space-y-2 mb-8">
            {mySpies.map((spy: any) => (
              <div key={spy.id} className="bg-surface-container-low border-l-2 border-violet-500/50 p-3 border border-white/5">
                <div className="flex justify-between items-center">
                  <span className="font-headline text-[11px] text-on-surface">{spy.name} <span className="text-zinc-500">Lv{Math.floor(spy.level)}</span></span>
                  <span className={`text-[9px] font-headline ${spy.status === "idle" ? "text-status-allied" : spy.status === "mission" ? "text-primary-container" : "text-error"}`}>{spy.status === "idle" ? "READY" : spy.status?.toUpperCase()}</span>
                </div>
                {spy.status === "idle" && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {["gatherIntel", "stealTech", "sabotage", "sowDissent", "counterIntel"].map(m => (
                      <button key={m} onClick={() => {
                        if (m === "counterIntel") { haptic("medium"); socketRef.current?.emit("send-spy-mission", { spyId: spy.id, mission: m, targetId: playerId }); }
                        else { setSpyPicker({ spyId: spy.id, mission: m }); }
                      }}
                        className="text-[8px] font-headline text-zinc-400 border border-white/10 px-1.5 py-0.5 uppercase hover:text-violet-400 hover:border-violet-400/30 transition-colors">
                        {{ gatherIntel: "INTEL", stealTech: "STEAL", sabotage: "SABOTAGE", sowDissent: "DISSENT", counterIntel: "DEFEND" }[m]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Chronicle */}
          <div className="flex items-center gap-2 mb-3 border-b border-white/5 pb-2">
            <div className="w-1 h-4 bg-primary-container" />
            <h2 className="font-headline text-sm tracking-widest text-primary-container">WORLD_CHRONICLE</h2>
          </div>
          <div className="space-y-2 pb-20">
            {chronicle.length === 0 && <p className="text-[10px] text-zinc-500">No entries yet.</p>}
            {chronicle.slice().reverse().slice(0, 20).map((entry: any, i: number) => (
              <div key={i} className="bg-surface-container p-3 border border-white/5">
                <h3 className="font-headline text-[11px] text-primary-container">{entry.message}</h3>
                <p className="text-[9px] font-label text-zinc-500 mt-0.5">Day {entry.day}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && <div className="fixed top-[7rem] left-1/2 -translate-x-1/2 z-50 bg-error-container text-on-error-container px-4 py-2 rounded font-label text-sm">{error}</div>}

      {/* Missile build menu */}
      {showMissileMenu && (
        <div className="ip-slide-up fixed bottom-14 left-0 w-full z-40 glass-panel border-t border-error/30 px-4 pt-3 pb-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-headline text-sm text-error tracking-widest">BUILD MISSILE</h2>
            <button onClick={() => setShowMissileMenu(null)}><MS icon="close" className="text-zinc-500 text-sm" /></button>
          </div>
          <div className="space-y-1.5">
            {[
              { type: "tactical", label: "TACTICAL", cost: "$200 + 50 munitions", color: "text-zinc-300", req: null },
              { type: "cruise", label: "CRUISE", cost: "$500 + 100 munitions", color: "text-primary-container", req: null },
              { type: "stealth", label: "STEALTH", cost: "$800 + 150 munitions + 50 electronics", color: "text-violet-400", req: "int2" },
              { type: "icbm", label: "ICBM", cost: "$1500 + 200 munitions + 100 fuel", color: "text-error", req: "mil3" },
              { type: "nuclear", label: "NUCLEAR", cost: "$5000 + 500 munitions + 100 uranium", color: "text-green-400", req: "mil4" },
            ].map(m => {
              const locked = m.req && !researchedTechs.includes(m.req);
            
  // Push notification subscription

  return (
                <div key={m.type} onClick={() => { if (!locked) { socketRef.current?.emit("build-missile", { siloQ: showMissileMenu.q, siloR: showMissileMenu.r, missileType: m.type }); setShowMissileMenu(null); } }}
                  className={`p-3 rounded border border-white/5 flex justify-between items-center ${locked ? "opacity-40" : "bg-surface-container-low hover:bg-surface-container cursor-pointer"} transition-all`}>
                  <div>
                    <span className={`font-headline text-[11px] ${m.color}`}>{m.label}</span>
                    <p className="text-[9px] font-label text-zinc-500">{m.cost}</p>
                  </div>
                  {locked && <span className="text-error text-[9px] font-headline flex items-center gap-0.5"><MS icon="lock" className="text-[10px]" />{m.req}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* === COMMANDERS TAB === */}
      {(activeTab === "commanders" || closingTab === "commanders") && (
        <div className={`${closingTab === "commanders" ? "ip-screen-out" : "ip-slide-up"} ip-panels-top fixed bottom-14 left-0 right-0 bg-background z-40 overflow-y-auto`} style={{ maxHeight: "calc(100vh - 10rem)" }}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary-container">military_tech</span>
                <h2 className="font-headline text-sm tracking-widest text-primary-container">COMMAND STAFF</h2>
                <span className="text-[10px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">{commanders.filter((c: any) => c.status !== "kia").length}/3</span>
              </div>
              {commanders.filter((c: any) => c.status !== "kia").length < 3 && (
                <button onClick={() => setShowRecruitMenu(!showRecruitMenu)} className="text-[10px] bg-primary-container/10 text-primary-container px-4 py-1.5 font-headline uppercase border border-primary-container/30 active:scale-95 transition-all">
                  {showRecruitMenu ? "CANCEL" : "+ RECRUIT"}
                </button>
              )}
            </div>

            {/* Recruit Menu */}
            {showRecruitMenu && (
              <div className="mb-4 p-3 bg-surface-container rounded-lg border border-primary-container/20">
                <p className="text-[10px] text-zinc-400 mb-3 font-label">Choose a specialty. Cost: $500 + 100 Steel + 50 Electronics</p>
                <div className="space-y-2">
                  {([
                    { id: "warfare", icon: "⚔️", desc: "Boosts attack power. Troops train faster. High-level ability: +50% attack & defense in 3-hex radius." },
                    { id: "logistics", icon: "📦", desc: "Boosts resource production on deployed hex. Faster construction. High-level ability: +50% production empire-wide." },
                    { id: "intelligence", icon: "🕵️", desc: "Extended vision range. Better counter-espionage. High-level ability: see all enemy troop movements." },
                    { id: "fortification", icon: "🛡️", desc: "Boosts defense. Better missile interception. High-level ability: hex cannot be captured while troops present." },
                    { id: "diplomacy", icon: "🤝", desc: "Slower war strain. Faster stability recovery. Better trade deals. High-level ability: force NAP on any player." },
                  ] as const).map(spec => (
                    <button key={spec.id} onClick={() => { haptic("medium"); sfxRecruit(); socketRef.current?.emit("recruit-commander", { specialty: spec.id }); setShowRecruitMenu(false); }}
                      className="w-full flex items-start gap-3 p-3 bg-surface-container-low/50 border border-white/5 rounded-lg text-left active:scale-[0.98] transition-all hover:border-primary-container/30">
                      <span className="text-xl mt-0.5">{spec.icon}</span>
                      <div className="flex-1">
                        <div className="font-headline text-[11px] text-on-surface capitalize">{spec.id}</div>
                        <div className="text-[9px] text-zinc-400 mt-0.5 leading-relaxed">{spec.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active Commanders */}
            {commanders.filter((c: any) => c.status !== "kia").length === 0 && !showRecruitMenu && (
              <div className="text-center py-8">
                <span className="material-symbols-outlined text-zinc-600 text-4xl">military_tech</span>
                <p className="text-zinc-500 text-[11px] font-label mt-2">No commanders recruited yet</p>
                <p className="text-zinc-600 text-[9px] mt-1">Commanders provide passive buffs to the hex they are deployed on</p>
              </div>
            )}

            <div className="space-y-3">
              {commanders.filter((c: any) => c.status !== "kia").map((cmd: any) => {
                const specIcons: Record<string, string> = { warfare: "⚔️", logistics: "📦", intelligence: "🕵️", fortification: "🛡️", diplomacy: "🤝" };
                const isRecovering = cmd.status === "recovering" && cmd.recoveryUntil && cmd.recoveryUntil > Date.now();
                const xpTable = [0, 100, 250, 500, 800, 1200, 1700, 2300, 3000, 4000];
                const xpNeeded = xpTable[cmd.level] || 9999;
                const xpPct = Math.min(100, (cmd.xp / xpNeeded) * 100);

                const abilityDescs: Record<string, { name: string; desc: string }> = {
                  blitz: { name: "Blitz Tactics", desc: "+25% attack for troops on this hex" },
                  veteranDrill: { name: "Veteran Drill", desc: "Troops train 40% faster" },
                  ironWill: { name: "Iron Will", desc: "Troops fight to the death (no retreat)" },
                  supremeCommand: { name: "Supreme Command", desc: "+50% ATK/DEF in 3-hex radius" },
                  efficientOps: { name: "Efficient Ops", desc: "+30% resource production on this hex" },
                  rapidBuild: { name: "Rapid Build", desc: "Buildings built 50% faster here" },
                  supplyLine: { name: "Supply Line", desc: "+15% production within 2 hexes" },
                  industrialGenius: { name: "Industrial Genius", desc: "+50% production empire-wide" },
                  reconMaster: { name: "Recon Master", desc: "+2 hex vision radius" },
                  counterOps: { name: "Counter Ops", desc: "+40% chance to catch enemy spies" },
                  saboteur: { name: "Saboteur", desc: "Cyber attacks cost 50% less" },
                  omniscience: { name: "Omniscience", desc: "See all enemy troop movements" },
                  bunkerDown: { name: "Bunker Down", desc: "+30% defense on this hex" },
                  missileBattery: { name: "Enhanced Battery", desc: "+20% missile intercept rate" },
                  citadel: { name: "Citadel Protocol", desc: "Hex can't be taken while troops here" },
                  aegisShield: { name: "Aegis Shield", desc: "90% intercept + reflect 25% damage" },
                  tradeBonus: { name: "Trade Envoy", desc: "+20% trade deal value" },
                  peacekeep: { name: "Peacekeeping", desc: "War strain grows 30% slower" },
                  propaganda: { name: "Propaganda", desc: "Stability recovers 40% faster" },
                  hegemon: { name: "Hegemonic Influence", desc: "Force NAP on any player (24h CD)" },
                };

                return (
                  <div key={cmd.id} className={"p-3 rounded-lg border " + (isRecovering ? "border-yellow-500/30 bg-yellow-900/10" : "border-white/10 bg-surface-container-low/50")}>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2.5">
                        <span className="text-xl">{specIcons[cmd.specialty] || "🎖️"}</span>
                        <div>
                          <div className="font-headline text-[12px] text-on-surface">{cmd.name}</div>
                          <div className="text-[9px] text-zinc-500">{cmd.title} · Level {cmd.level} · <span className="capitalize">{cmd.specialty}</span></div>
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        {isRecovering ? (
                          <span className="text-[9px] text-yellow-400 font-headline bg-yellow-900/20 px-2 py-1 rounded">RECOVERING</span>
                        ) : cmd.assignedHex ? (
                          <button onClick={() => { socketRef.current?.emit("unassign-commander", { commanderId: cmd.id }); }}
                            className="text-[9px] text-red-400 border border-red-500/30 px-2.5 py-1 font-headline active:scale-95">RECALL</button>
                        ) : (
                          <button onClick={() => { haptic("medium"); setTargetingMode("commander:" + cmd.id); setActiveTab(null); }}
                            className="text-[9px] text-green-400 border border-green-500/30 px-2.5 py-1 font-headline active:scale-95">DEPLOY</button>
                        )}
                        <button onClick={() => { if (confirm("Dismiss " + cmd.name + "? This is permanent.")) { haptic("heavy"); socketRef.current?.emit("dismiss-commander", { commanderId: cmd.id }); } }}
                          className="text-[9px] text-zinc-600 border border-zinc-700 px-2 py-1 font-headline active:scale-95">✕</button>
                      </div>
                    </div>

                    {/* XP Bar */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-primary-container/60 rounded-full transition-all duration-500" style={{ width: xpPct + "%" }}></div>
                      </div>
                      <span className="text-[8px] text-zinc-500 font-mono">{cmd.xp}/{xpNeeded} XP</span>
                    </div>

                    {/* Stats with explanations */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[9px] mb-2">
                      <div className="flex items-center gap-1"><span className="text-red-400 font-headline">⚔ {cmd.stats.attack}</span><span className="text-zinc-600">combat damage</span></div>
                      <div className="flex items-center gap-1"><span className="text-blue-400 font-headline">🛡 {cmd.stats.defense}</span><span className="text-zinc-600">damage resist</span></div>
                      <div className="flex items-center gap-1"><span className="text-green-400 font-headline">📦 {cmd.stats.logistics}</span><span className="text-zinc-600">resource boost</span></div>
                      <div className="flex items-center gap-1"><span className="text-purple-400 font-headline">💫 {cmd.stats.inspiration}</span><span className="text-zinc-600">troop capacity</span></div>
                    </div>

                    {/* Location */}
                    {cmd.assignedHex && (
                      <div className="text-[9px] text-green-400/80 mb-2 flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs">location_on</span>
                        Deployed at ({cmd.assignedHex.q}, {cmd.assignedHex.r})
                      </div>
                    )}

                    {/* Abilities */}
                    <div className="space-y-1">
                      {cmd.abilities.map((aId: string) => {
                        const ab = abilityDescs[aId];
                        return ab ? (
                          <div key={aId} className="flex items-center gap-2 bg-primary-container/5 border border-primary-container/10 rounded px-2 py-1">
                            <span className="text-[9px] text-primary-container font-headline">{ab.name}</span>
                            <span className="text-[8px] text-zinc-500">{ab.desc}</span>
                          </div>
                        ) : null;
                      })}
                      {/* Show locked abilities */}
                      {[3, 5, 8].filter(lvl => lvl > cmd.level).map(lvl => (
                        <div key={lvl} className="flex items-center gap-2 bg-zinc-900/30 border border-zinc-800 rounded px-2 py-1 opacity-40">
                          <span className="text-[9px] text-zinc-500 font-headline">Level {lvl}</span>
                          <span className="text-[8px] text-zinc-600">Locked</span>
                        </div>
                      ))}
                    </div>

                    {/* Combat record */}
                    <div className="flex gap-3 mt-2 text-[8px] text-zinc-600">
                      <span>Battles: {cmd.combatsSurvived}</span>
                      <span>Kills: {cmd.killCount}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* KIA Memorial */}
            {commanders.filter((c: any) => c.status === "kia").length > 0 && (
              <div className="mt-4 pt-3 border-t border-white/5">
                <span className="text-[9px] text-zinc-600 uppercase font-headline tracking-wider">Fallen in Service</span>
                {commanders.filter((c: any) => c.status === "kia").map((cmd: any) => (
                  <div key={cmd.id} className="text-[10px] text-zinc-600 mt-1">✝ {cmd.name} — {cmd.title} (Lv.{cmd.level})</div>
                ))}
              </div>
            )}

            {/* How it works */}
            <div className="mt-4 p-3 bg-surface-container rounded-lg border border-white/5">
              <div className="font-headline text-[10px] text-zinc-400 uppercase tracking-wider mb-2">How Commanders Work</div>
              <div className="space-y-1 text-[9px] text-zinc-500 leading-relaxed">
                <p>• Deploy a commander to one of your hexes for passive bonuses</p>
                <p>• They gain XP from combat, building, research, and time deployed</p>
                <p>• New abilities unlock at levels 3, 5, and 8</p>
                <p>• If you lose a battle where they are deployed, they risk death (permanent)</p>
                <p>• Dismiss a commander to free the slot for a different specialty</p>
              </div>
            </div>
          </div>
        </div>
      )}

n      {/* MARKETPLACE */}
      {(activeTab === "market" || closingTab === "market") && (
        <MarketplacePage socket={socketRef.current} player={player} />
      )}
      
      {/* SPY TARGET PICKER */}
      {spyPicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSpyPicker(null)}>
          <div className="bg-surface-container/95 border border-violet-500/30 rounded-xl p-4 w-72 shadow-2xl" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="text-center mb-3">
              <span className="font-headline text-[11px] text-violet-400 uppercase tracking-wider">Select Target</span>
              <div className="text-[9px] text-zinc-500 mt-0.5">Mission: {{ gatherIntel: "GATHER INTEL", stealTech: "STEAL TECH", sabotage: "SABOTAGE", sowDissent: "SOW DISSENT" }[spyPicker.mission] || spyPicker.mission.toUpperCase()}</div>
            </div>
            <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
              {otherPlayers.filter(p => p.id !== playerId).map(p => (
                <button key={p.id} onClick={() => {
                  haptic("medium");
                  socketRef.current?.emit("send-spy-mission", { spyId: spyPicker.spyId, mission: spyPicker.mission, targetId: p.id });
                  setSpyPicker(null);
                }}
                  className="flex items-center justify-between p-2.5 bg-violet-900/10 border border-violet-500/20 rounded-lg active:scale-95 transition-all">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                    <span className="font-headline text-[11px] text-zinc-200">{p.name}</span>
                  </div>
                  <span className={`text-[9px] ${p.online ? "text-green-400" : "text-zinc-600"}`}>{p.online ? "ONLINE" : "OFFLINE"}</span>
                </button>
              ))}
              {otherPlayers.filter(p => p.id !== playerId).length === 0 && (
                <p className="text-[10px] text-zinc-500 text-center py-4">No other players found</p>
              )}
            </div>
            <button onClick={() => setSpyPicker(null)} className="mt-3 w-full text-[9px] text-zinc-500 font-headline uppercase py-1.5 border border-white/10 active:scale-95">CANCEL</button>
          </div>
        </div>
      )}

      {/* MISSILE CONFIRMATION */}
      {pendingConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setPendingConfirm(null); setTargetingMode(null); }}>
          <div className="bg-surface-container border border-white/10 rounded-lg p-5 mx-4 max-w-sm w-full shadow-2xl" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="text-center">
              <div className="text-2xl mb-2">{pendingConfirm.action === "demolish" ? "🔨" : pendingConfirm.action === "attack" ? "⚔️" : pendingConfirm.action === "upgrade" ? "⬆️" : pendingConfirm.type === "nuclear" ? "☢️" : "🚀"}</div>
              <h3 className="font-headline text-on-surface text-sm uppercase tracking-wider mb-1">{pendingConfirm.action === "demolish" ? "Confirm Demolish" : pendingConfirm.action === "attack" ? "Confirm Attack" : pendingConfirm.action === "upgrade" ? "Confirm Upgrade" : "Confirm Launch"}</h3>
              <p className="text-primary-container text-xs font-label mb-4">
                {pendingConfirm.action === "demolish"
                  ? <>Demolish <span className="text-primary font-headline">{pendingConfirm.name}</span>? This cannot be undone.</>
                  : pendingConfirm.action === "attack" 
                  ? <>Send <span className="text-primary font-headline">{pendingConfirm.troops}</span> troops to attack hex ({pendingConfirm.toQ}, {pendingConfirm.toR})?</>
                  : pendingConfirm.action === "upgrade"
                  ? <>Upgrade <span className="text-primary font-headline">{pendingConfirm.buildingType}</span> to <span className="text-orange-400 font-headline">Level {pendingConfirm.level}</span>?{pendingConfirm.costStr && <><br/><span className="text-zinc-400 text-[10px]">Cost: {pendingConfirm.costStr}</span></>}</>
                  : <>Launch <span className="text-primary font-headline uppercase">{pendingConfirm.type}</span> missile at hex ({pendingConfirm.targetQ}, {pendingConfirm.targetR})?</>
                }
              </p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => { setPendingConfirm(null); setTargetingMode(null); }} className="px-5 py-2 border border-white/20 text-on-surface font-headline text-[10px] uppercase tracking-wider active:scale-95 transition-all">CANCEL</button>
                {pendingConfirm.action === "demolish" ? (
                  <button onClick={() => { const p = pendingConfirm; haptic("heavy"); sfxAttack(); socketRef.current?.emit("demolish", { q: p.q, r: p.r, buildingIndex: p.buildingIndex }); setPendingConfirm(null); }} className="px-5 py-2 bg-error/90 text-background font-headline text-[10px] uppercase tracking-wider active:scale-95 transition-all">🔨 DEMOLISH</button>
                ) : pendingConfirm.action === "upgrade" ? (
                  <button onClick={() => { const p = pendingConfirm; haptic("medium"); sfxBuild(); socketRef.current?.emit("upgrade-building", { q: p.q, r: p.r, buildingIndex: p.buildingIndex }); setPendingConfirm(null); }} className="px-5 py-2 bg-orange-600/90 text-background font-headline text-[10px] uppercase tracking-wider active:scale-95 transition-all">⬆️ UPGRADE</button>
                ) : pendingConfirm.action === "attack" ? (
                  <button onClick={() => { const p = pendingConfirm; haptic("heavy"); sfxAttack(); screenShake(false); socketRef.current?.emit("attack-hex", { fromQ: p.fromQ, fromR: p.fromR, toQ: p.toQ, toR: p.toR, troops: p.troops }); setPendingConfirm(null); setSelectedTile(null); }} className="px-5 py-2 bg-error/90 text-background font-headline text-[10px] uppercase tracking-wider active:scale-95 transition-all">⚔️ ATTACK</button>
                ) : (
                  <button onClick={() => { const p = pendingConfirm; haptic("heavy"); sfxMissileLaunch(); socketRef.current?.emit("launch-missile", { siloQ: p.siloQ, siloR: p.siloR, targetQ: p.targetQ, targetR: p.targetR, missileType: p.type }); setPendingConfirm(null); setTargetingMode(null); }} className="px-5 py-2 bg-error/90 text-background font-headline text-[10px] uppercase tracking-wider active:scale-95 transition-all">🚀 LAUNCH</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RADIAL HEX MENU */}
      {radialMenu && (
        <div className="fixed inset-0 z-[9998]" onClick={() => setRadialMenu(null)}>
          <div className="absolute" style={{ left: radialMenu.x, top: radialMenu.y, transform: "translate(-50%, -50%)" }}>
            {/* Center label */}
            <div className="absolute -translate-x-1/2 -translate-y-1/2 bg-surface-container/95 backdrop-blur-sm border border-orange-500/30 rounded-full w-12 h-12 flex items-center justify-center">
              <span className="text-[8px] font-headline text-on-surface">{radialMenu.q},{radialMenu.r}</span>
            </div>
            {/* Action buttons arranged in a circle */}
            {(() => {
              const t = radialMenu.tile;
              const isOwn = t?.ownerId === playerId;
              const buildings = t?.buildings || [];
              const hasBarracks = buildings.some((b: any) => b.type === "barracks");
              const hasSilo = buildings.some((b: any) => b.type === "missileSilo");
              const hasTroops = (t?.troops || 0) > 0;
              const isEmpty = buildings.length === 0;
              const items: any[] = [];

              if (isOwn) {
                if (isEmpty) items.push({ icon: "construction", label: "BUILD", action: () => { setSelectedTile(t); setShowBuildMenu(true); setRadialMenu(null); } });
                if (hasBarracks) items.push({ icon: "groups", label: "TRAIN", action: () => { optimisticTrain(radialMenu.q, radialMenu.r, 1); socketRef.current?.emit("train-troops", { q: radialMenu.q, r: radialMenu.r, count: 1 }); setRadialMenu(null); } });
                if (hasSilo) items.push({ icon: "rocket_launch", label: "SILO", action: () => { setSelectedTile(t); setRadialMenu(null); } });
                if (hasTroops) items.push({ icon: "open_with", label: "MOVE", action: () => { setTargetingMode("move"); setMoveFrom({ q: radialMenu.q, r: radialMenu.r, count: t?.troops || 0 }); setRadialMenu(null); } });
                const upgradeable = buildings.find((b: any) => (b.level || 1) < 3 && (!b.completedAt || b.completedAt <= Date.now()));
                if (upgradeable) {
                  const upIdx = buildings.indexOf(upgradeable);
                  const bCfg = BUILDING_CONFIGS[upgradeable.type as keyof typeof BUILDING_CONFIGS];
                  const nextLv = (upgradeable.level || 1) + 1;
                  const costMult = [1, 2, 4][nextLv - 1];
                  const costStr = bCfg ? Object.entries(bCfg.cost).map(([r, a]: any) => `${r}: ${a * costMult}`).join(", ") : "";
                  items.push({ icon: "arrow_upward", label: `LV${nextLv}`, action: () => {
                    setPendingConfirm({ action: "upgrade", q: radialMenu.q, r: radialMenu.r, buildingIndex: upIdx, buildingType: bCfg?.name || upgradeable.type, level: nextLv, costStr });
                    setRadialMenu(null);
                  }});
                }
                items.push({ icon: "info", label: "INFO", action: () => { setSelectedTile(t); setRadialMenu(null); } });
              } else if (t?.ownerId) {
                items.push({ icon: "swords", label: "ATTACK", action: () => { setSelectedTile(t); setRadialMenu(null); } });
                // MISSILE — find any silo with missiles
                const allSiloKeys = Object.keys(siloInventory);
                const loadedSilo = allSiloKeys.find(k => siloInventory[k]?.length > 0);
                if (loadedSilo) {
                  items.push({ icon: "rocket_launch", label: "MISSILE", action: () => {
                    setRadialMenu(null);
                    setMissilePicker({ targetQ: radialMenu.q, targetR: radialMenu.r, silos: allSiloKeys.filter(k => siloInventory[k]?.length > 0) });
                  }});
                }
                items.push({ icon: "info", label: "INFO", action: () => { setSelectedTile(t); setRadialMenu(null); } });
              } else {
                items.push({ icon: "flag", label: "CLAIM", action: () => { optimisticClaim(radialMenu.q, radialMenu.r); socketRef.current?.emit(SOCKET_EVENTS.CLAIM_HEX, { q: radialMenu.q, r: radialMenu.r }); setRadialMenu(null); } });
                items.push({ icon: "info", label: "INFO", action: () => { setSelectedTile(t); setRadialMenu(null); } });
              }

              // Distribute angles evenly
              const step = 360 / items.length;
              const startAngle = -90;
              return items.map((btn: any, i: number) => ({ ...btn, angle: startAngle + i * step }));
            })().map((btn, i) => {
              const rad = (btn.angle * Math.PI) / 180;
              const dist = 55;
              const bx = Math.cos(rad) * dist;
              const by = Math.sin(rad) * dist;
              return (
                <button key={i} onClick={(e) => { e.stopPropagation(); btn.action(); }}
                  className="absolute w-11 h-11 -translate-x-1/2 -translate-y-1/2 bg-surface-container/95 backdrop-blur-sm border border-orange-500/40 rounded-full flex flex-col items-center justify-center active:scale-90 transition-all shadow-lg"
                  style={{ left: bx, top: by }}>
                  <span className="material-symbols-rounded text-[16px] text-orange-400">{btn.icon}</span>
                  <span className="text-[6px] font-headline text-zinc-400 leading-none">{btn.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* === MISSILE PICKER (from radial) === */}
      {missilePicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setMissilePicker(null)}>
          <div className="bg-surface-container/95 border border-orange-500/30 rounded-xl p-4 w-72 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-3">
              <span className="font-headline text-[11px] text-orange-400 uppercase tracking-wider">Launch Missile</span>
              <div className="text-[9px] text-zinc-500 mt-0.5">Target: ({missilePicker.targetQ}, {missilePicker.targetR})</div>
            </div>
            <div className="flex flex-col gap-2">
              {missilePicker.silos.map((siloKey: string) => {
                const inv = siloInventory[siloKey] || [];
                const [sq, sr] = siloKey.split(",").map(Number);
                // Group by type
                const counts: Record<string, number> = {};
                inv.forEach((t: string) => { counts[t] = (counts[t] || 0) + 1; });
                return Object.entries(counts).map(([mtype, count]) => (
                  <button key={siloKey + mtype}
                    onClick={() => {
                      const isNuke = mtype === "nuclear";
                      setPendingConfirm({
                        action: "missile",
                        type: mtype,
                        siloQ: sq, siloR: sr,
                        targetQ: missilePicker.targetQ, targetR: missilePicker.targetR,
                        header: isNuke ? "☢️ NUCLEAR LAUNCH" : "🚀 MISSILE LAUNCH",
                        body: `Launch ${mtype.toUpperCase()} from silo (${sq},${sr}) at target (${missilePicker.targetQ},${missilePicker.targetR})?`,
                      });
                      setMissilePicker(null);
                    }}
                    className="flex items-center justify-between p-2.5 bg-error/10 border border-error/30 rounded-lg active:scale-95 transition-all">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{mtype === "nuclear" ? "☢️" : "🚀"}</span>
                      <div className="text-left">
                        <div className="font-headline text-[10px] text-error uppercase">{mtype}</div>
                        <div className="text-[8px] text-zinc-500">Range: {MISSILE_CONFIG[mtype as MissileType]?.range === -1 ? '∞' : MISSILE_CONFIG[mtype as MissileType]?.range} · DMG: {MISSILE_CONFIG[mtype as MissileType]?.damage}</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-headline text-zinc-400">×{count}</span>
                  </button>
                ));
              })}
            </div>
            <button onClick={() => setMissilePicker(null)} className="w-full mt-3 py-1.5 text-[9px] font-headline text-zinc-500 uppercase border border-zinc-700/30 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      {/* === BOTTOM NAV === */}
      <nav className="fixed bottom-0 left-0 w-full z-50 flex justify-around items-center pb-safe h-14 bg-background/95 backdrop-blur-2xl border-t border-orange-500/30 shadow-[0_-4px_20px_rgba(0,0,0,0.5)]">
        {[
          { id: null, label: "MAP", icon: "map" },
          { id: "chat", label: "CHAT", icon: "forum" },
          { id: "tech", label: "TECH", icon: "memory" },
          { id: "diplomacy", label: "DIPLO", icon: "handshake" },
          { id: "events", label: "INTEL", icon: "detector" },
          { id: "commanders", label: "CMD", icon: "military_tech" },
          { id: "market", label: "MARKET", icon: "storefront" },
        ].map(tab => (
          <button key={tab.label} onClick={() => tab.id === null ? (setActiveTab(null), setSelectedTile(null)) : switchTab(tab.id)}
            className={`flex flex-col items-center justify-center px-2 h-full transition-all duration-200 active:scale-90 ${activeTab === tab.id ? "text-orange-500 border-t-2 border-orange-500 bg-orange-500/10" : "text-zinc-500 hover:text-orange-400"}`}>
            <MS icon={tab.icon} filled={activeTab === tab.id} />
            <span className="font-label font-bold text-[10px] tracking-tighter mt-0.5">{tab.label}</span>
            {tab.id === "chat" && unreadChat > 0 && <span className="absolute top-1 right-[15%] w-4 h-4 bg-error rounded-full text-[8px] text-white flex items-center justify-center font-bold">{unreadChat > 9 ? "9+" : unreadChat}</span>}
            {tab.id === "events" && unreadIntel > 0 && <span className="absolute top-1 right-[15%] w-4 h-4 bg-error rounded-full text-[8px] text-white flex items-center justify-center font-bold">{unreadIntel > 9 ? "9+" : unreadIntel}</span>}
          </button>
        ))}
      </nav>
    </div>
  );
}
